import { Environment } from "@react-three/drei";
import { Canvas, extend, useFrame, useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { bloom } from "three/examples/jsm/tsl/display/BloomNode.js";
import { ao } from "three/examples/jsm/tsl/display/GTAONode.js";
import {
  float,
  max,
  min,
  mix,
  mrt,
  normalView,
  output,
  pass,
  screenSize,
  screenUV,
  smoothstep,
  step,
  uniform,
  vec2,
  vec4,
} from "three/tsl";
import * as THREE from "three/webgpu";
import { PostProcessing as ThreePostProcessing } from "three/webgpu";
import {
  CAMERA_KEYS,
  isEditableField,
  ZOOM_IN_KEYS,
  ZOOM_OUT_KEYS,
} from "../app/keymap.ts";
import { subscribeLiveEditPreview } from "../app/liveEditPreview.ts";
import {
  selectActiveRenderConfig,
  useActiveRenderConfig,
  useActiveRenderConfigShallow,
} from "../app/renderConfig.ts";
import { useStore } from "../app/store.ts";
import type { AppState, CameraProjection } from "../app/storeTypes.ts";
import { isObjectVisibleInRenderOutput } from "../app/visibilityPolicy.ts";
import {
  type InvalidateReason,
  recordInvalidate,
} from "../debug/listenerProbe.ts";
import { isLightObject, type PolygonTopology } from "../modeling/types.ts";
import {
  registerViewportSurfaceRaycast,
  type ViewportSurfaceHit,
} from "../modeling/viewportSurfaceRaycast.ts";
import { GRID_EXTENT } from "../units.ts";
import { AxisIndicator } from "./AxisIndicator.tsx";
import { BackgroundImage } from "./BackgroundImage.tsx";
import { BoxSelectHandler } from "./BoxSelectHandler.tsx";
import type { BrushCursorState } from "./BrushCursor.tsx";
import { BrushSelectHandler } from "./BrushSelectHandler.tsx";
import {
  flushCameraSync,
  type LiveCameraRead,
  markCameraDirty,
  notifyCameraGestureStart,
  registerCameraSync,
} from "./cameraSync.ts";
import {
  CameraTransitionDriver,
  isCameraTransitioning,
} from "./cameraTransition.ts";
import { EditModeRenderer } from "./EditModeRenderer.tsx";
import { EntityRenderer } from "./EntityRenderer.tsx";
import { EyedropperHandler } from "./EyedropperHandler.tsx";
import { resetEditSelectionCycle } from "./editSelectionCycle.ts";
import { ENV_MAP_PRESETS } from "./envPresets.ts";
import { pruneConstrainedPositions } from "./followConstraintRegistry.ts";
import { SceneGizmoLayer } from "./gizmo/SceneGizmoLayer.tsx";
import { SnapIndicator } from "./gizmo/SnapIndicator.tsx";
import { INKING_LAYER_BY_CATEGORY } from "./inkingCategories.ts";
import {
  syncInkingCameraPlanes,
  syncInkingUniforms,
} from "./inkingUniforms.ts";
import { getInputScale } from "./inputScale.ts";
import { MultiTouchHandler } from "./MultiTouchHandler.tsx";
import {
  navIntentFromMousePointer,
  navIntentFromTouchGesture,
  type ViewportNavIntent,
} from "./navIntent.ts";
import { OriginDots } from "./OriginDots.tsx";
import { REALTIME_CANVAS_SHADOWS } from "./realtimeShadows.ts";
import { applyDisplayColorPipeline } from "./renderCalibration.ts";
import { SceneLights } from "./SceneLights.tsx";
import { ScreenspaceSketchOverlay } from "./ScreenspaceSketchOverlay.tsx";
import { SculptCursor } from "./SculptCursor.tsx";
import { SculptHandler } from "./SculptHandler.tsx";
import { SketchCursor } from "./SketchCursor.tsx";
import { SketchHandler } from "./SketchHandler.tsx";
import { SnapRaycastProvider } from "./SnapRaycastProvider.tsx";
import { StrokeOverlay } from "./StrokeOverlay.tsx";
import {
  SurfaceBoxHandler,
  SurfaceCylinderHandler,
} from "./SurfaceBoxHandler.tsx";
import { SurfaceInsetHandler } from "./SurfaceInsetHandler.tsx";
import { SurfacePolyHandler } from "./SurfacePolyHandler.tsx";
import { screenToWorldRadius } from "./screenRadius.ts";
import { resetSelectionCycle } from "./selectionCycle.ts";
import {
  ACTIVE_GENERATOR_SELECTION_OUTLINE_COLOR,
  ACTIVE_INSTANCE_SELECTION_OUTLINE_COLOR,
  ACTIVE_SELECTION_OUTLINE_COLOR,
  GENERATOR_SELECTION_OUTLINE_COLOR,
  INSTANCE_SELECTION_OUTLINE_COLOR,
  pruneSelectionPrepassMaterials,
  SELECTION_OUTLINE_COLOR,
  SELECTION_OUTLINE_LAYER,
} from "./selectionOutlinePass.ts";
import { classifyViewLockOrbitChange } from "./viewLockOrbit.ts";
import { drawViewportBgPreset } from "./viewportBgPresets.ts";
import { viewportSupportsSceneInteraction } from "./viewportInteractionPolicy.ts";

// Register three/webgpu classes with R3F's JSX element catalogue.
// Without this, R3F's intrinsic elements (<mesh>, <boxGeometry>, etc.)
// map to the default "three" classes rather than the WebGPU-aware versions.
extend(THREE as any);

// Patch InstancedBufferGeometry so instanceCount never stays at Infinity.
// WebGPU's drawIndexed() rejects Infinity as a vertex count, crashing on
// any InstancedBufferGeometry (including LineSegmentsGeometry) that hasn't
// had setPositions() called yet.  We intercept the property setter so that
// any write of Infinity is silently clamped to 0.  The correct count is
// always set later by setPositions / setMatrixAt / etc.
{
  const storage = new WeakMap<THREE.InstancedBufferGeometry, number>();
  Object.defineProperty(
    THREE.InstancedBufferGeometry.prototype,
    "instanceCount",
    {
      get(this: THREE.InstancedBufferGeometry) {
        return storage.get(this) ?? 0;
      },
      set(this: THREE.InstancedBufferGeometry, v: number) {
        storage.set(this, Number.isFinite(v) ? v : 0);
      },
      configurable: true,
      enumerable: true,
    },
  );
}

import { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { type CameraKeys, flyMouseDelta } from "../app/useKeyboardShortcuts.ts";
import {
  groundGridMaterialXY,
  groundGridMaterialXZ,
  groundGridMaterialYZ,
  setGroundGridLogDist,
} from "./groundGridMaterial.ts";
import { initMaxAnisotropy } from "./materialCache.ts";
import { initShadingDiag } from "./shadingDiag.ts";
import { useAdaptivePerformance } from "./useAdaptivePerformance.ts";

/**
 * Blender-consistent Z-up coordinate system.
 *
 * Blender: X=right, Y=forward, Z=up
 * We match this exactly — no axis swapping on import.
 */
const WORLD_UP = new THREE.Vector3(0, 0, 1);

// ---------------------------------------------------------------------------
// Navigation raycast suppression
// ---------------------------------------------------------------------------
// R3F raycasts the scene on pointer events to dispatch its event system.
// With BVH acceleration this is O(log n), so pointerdown/pointerup raycasts
// are fast enough to always allow — this ensures tap-to-select works reliably.
//
// During active navigation drags (orbit/pan/dolly), we suppress pointermove
// raycasts to avoid unnecessary intersection tests while the user is simply
// moving the camera.  The flag is set once the pointer moves beyond the tap
// threshold and cleared on pointerup.
// ---------------------------------------------------------------------------

const NAV_TAP_THRESHOLD_SQ = 36; // 6 px — matches SceneRenderer TAP_THRESHOLD

/** When true, the R3F events.filter returns [] to skip raycasting. */
let _suppressRaycast = false;
let _navDownPos: { x: number; y: number } | null = null;

/** Default camera: Blender-style front view, slightly elevated */
export const DEFAULT_CAMERA_POS: [number, number, number] = [0, -3, 1];
export const DEFAULT_CAMERA_TARGET: [number, number, number] = [0, 0, 0.5];

/** Compute vertical FOV from focal length (mm) using 24mm sensor height. */
export function focalLengthToFov(focalLength: number): number {
  const sensorHeight = 24;
  return (2 * Math.atan(sensorHeight / (2 * focalLength)) * 180) / Math.PI;
}

/** Check if an object or any of its ancestors has excludeFromExport. */
function hasExcludeTag(obj: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = obj;
  while (node) {
    if (node.userData?.excludeFromExport) return true;
    node = node.parent;
  }
  return false;
}

function hasPaintOverlayTag(obj: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = obj;
  while (node) {
    if (node.userData?.sketchOverlay) return true;
    node = node.parent;
  }
  return false;
}

function shouldHideForExportCapture(
  obj: THREE.Object3D,
  includePaintOverlays: boolean,
): boolean {
  if (includePaintOverlays && hasPaintOverlayTag(obj)) return false;
  return hasExcludeTag(obj);
}

function resolveSurfaceObjectId(obj: THREE.Object3D): string | null {
  let node: THREE.Object3D | null = obj;
  while (node) {
    const objectId = node.userData?.poserefObjectId;
    if (typeof objectId === "string" && objectId.length > 0) return objectId;
    node = node.parent;
  }
  return null;
}

const EMPTY_SURFACE_EXCLUDE_IDS = new Set<string>();

function syncInkingCameraPlanesFromSource(source: THREE.Camera): void {
  const near = (source as any).near;
  const far = (source as any).far;
  if (typeof near === "number" && typeof far === "number") {
    syncInkingCameraPlanes(near, far);
  }
}

function syncSelectionOutlineCamera(
  camera: THREE.Camera,
  source: THREE.Camera,
): void {
  camera.copy(source as any, false);
  camera.layers.disableAll();
  camera.layers.enable(SELECTION_OUTLINE_LAYER);
  camera.updateMatrixWorld();
}

// AO renders into a dedicated scene pass with MRT {output, normal} so we
// can feed real view-space normals into GTAONode. We can't attach MRT to
// the main scenePass because some overlay materials (wireframe, bones,
// MeshBasicMaterial, etc.) stay on layer 0 and don't emit a writeMask for
// the second color attachment — WebGPU pipeline creation rejects that
// with "THREE.writeMask is invalid". Core scene geometry (primitives,
// mannequins, blend objects) is already tagged onto the inking-category
// layers by `markInkingCategory`, so enabling all three layers on a
// dedicated AO camera renders exactly the MRT-compatible meshes and skips
// the overlays regardless of the user's inking include-toggles.
function syncAoCamera(camera: THREE.Camera, source: THREE.Camera): void {
  camera.copy(source as any, false);
  camera.layers.disableAll();
  camera.layers.enable(INKING_LAYER_BY_CATEGORY.primitive);
  camera.layers.enable(INKING_LAYER_BY_CATEGORY.mannequin);
  camera.layers.enable(INKING_LAYER_BY_CATEGORY.blend);
  camera.updateMatrixWorld();
}

/**
 * Exposes a window function that renders one frame with a transparent
 * background for PNG export (no scene background, no ref image).
 */

/**
 * Crop the center square of a canvas, scale to `size`, and return a WebP blob.
 * Used by captureThumbnail to produce a consistent square thumbnail.
 */
function blobFromCanvasCenter(
  canvas: HTMLCanvasElement,
  size: number,
  backgroundPreset?: string,
): Promise<Blob | null> {
  const offscreen = document.createElement("canvas");
  offscreen.width = size;
  offscreen.height = size;
  const ctx = offscreen.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  if (backgroundPreset) {
    drawViewportBgPreset(ctx, backgroundPreset, size, size);
  }
  const srcSize = Math.min(canvas.width, canvas.height);
  const sx = (canvas.width - srcSize) / 2;
  const sy = (canvas.height - srcSize) / 2;
  ctx.drawImage(canvas, sx, sy, srcSize, srcSize, 0, 0, size, size);
  return new Promise((resolve) => offscreen.toBlob(resolve, "image/webp", 0.8));
}

function ExportHelper() {
  const { gl, scene, camera } = useThree();
  const exportCanvasRef = useRef<HTMLCanvasElement | null>(null);

  /**
   * Hide gizmos, grid, empties, and render-hidden objects for export.
   * Returns a restore function that re-shows everything.
   * Sketch overlays stay hidden by default for thumbnails; full rendered
   * image export opts in because strokes are authored scene content.
   *
   * When `isolateObjectIds` is provided, additionally hide every owning
   * scene object that is not in the set and is not a light — used by the
   * library asset thumbnail capture so the saved preview shows only the
   * asset, lit by the existing scene lights.
   */
  const hideExportExclusions = useCallback(
    (
      isolateObjectIds?: ReadonlySet<string> | null,
      includePaintOverlays = false,
    ): (() => void) => {
      const hidden: THREE.Object3D[] = [];
      const storeState = useStore.getState();
      const renderVis = storeState.renderVisibility;
      const sceneObjects = storeState.sceneObjects;
      scene.traverse((obj) => {
        if (!obj.visible) return;
        if (shouldHideForExportCapture(obj, includePaintOverlays)) {
          obj.visible = false;
          hidden.push(obj);
          return;
        }
        if (
          (obj as any).isMesh &&
          (obj as any).material?.type === "GridMaterial"
        ) {
          obj.visible = false;
          hidden.push(obj);
          return;
        }
        if (
          obj.name &&
          !isObjectVisibleInRenderOutput({
            visibility: storeState.visibility,
            renderVisibility: renderVis,
            keys: [obj.userData?.poserefObjectId, obj.name],
          })
        ) {
          obj.visible = false;
          hidden.push(obj);
          return;
        }
        if (obj.name && sceneObjects.get(obj.name)?.objectKind === "empty") {
          obj.visible = false;
          hidden.push(obj);
          return;
        }
        if (isolateObjectIds) {
          // Resolve via userData first (set on primitive/mannequin/blend
          // roots); fall back to obj.name (used by the LightNode group and
          // legacy renderers). If neither resolves we don't hide — the node
          // belongs to a non-asset bit of scaffolding (e.g. a Three.js
          // pointLight inside a LightNode) that the existing rules above
          // already cover when appropriate.
          const ownerId = resolveSurfaceObjectId(obj) ?? obj.name;
          const ownerObj = ownerId ? sceneObjects.get(ownerId) : undefined;
          if (
            ownerObj &&
            !isolateObjectIds.has(ownerObj.id) &&
            !isLightObject(ownerObj)
          ) {
            obj.visible = false;
            hidden.push(obj);
            return;
          }
        }
      });
      return () => {
        for (const obj of hidden) obj.visible = true;
      };
    },
    [scene],
  );

  /**
   * Render at the exact target export resolution. Temporarily bumps the
   * renderer DPR so the frame crop region has enough physical pixels, renders,
   * crops the frame from the high-DPR canvas, then restores immediately.
   *
   * This avoids camera.setViewOffset() (which warps aspect ratio when the
   * PostProcessing pipeline caches internal RT sizes) by keeping the camera
   * and logical viewport unchanged — only the pixel density increases.
   *
   * The crop rectangle is derived from the frame element's *center* plus
   * the target aspect — never from `frameRect.height` directly — so a
   * fraction-of-a-pixel drift in CSS layout cannot leak into the export.
   * This guarantees the on-screen frame extents match the exported file
   * pixel-for-pixel.
   */
  const renderAtTargetRes = useCallback(
    (renderFn: () => void, options?: { backgroundPreset?: string | null }) => {
      const backgroundPreset = options?.backgroundPreset ?? null;
      const { renderWidth, renderHeight } = useStore.getState();
      const target = { width: renderWidth, height: renderHeight };
      const targetAspect = target.width / target.height;

      const frame = document.querySelector("[data-camera-frame]");
      if (!frame) {
        // No frame overlay — render at viewport size, fall through to cropToFrame
        renderFn();
        if (backgroundPreset) {
          if (!exportCanvasRef.current) {
            exportCanvasRef.current = document.createElement("canvas");
          }
          const offscreen = exportCanvasRef.current;
          offscreen.width = gl.domElement.width;
          offscreen.height = gl.domElement.height;
          const ctx = offscreen.getContext("2d");
          if (ctx) {
            drawViewportBgPreset(
              ctx,
              backgroundPreset,
              offscreen.width,
              offscreen.height,
            );
            ctx.drawImage(gl.domElement, 0, 0);
          }
          window.__poseref_exportCanvas = offscreen;
        }
        return;
      }

      const canvasRect = gl.domElement.getBoundingClientRect();
      const frameRect = frame.getBoundingClientRect();

      // Derive the CSS crop rect from the frame's center and the target
      // aspect, not from (frameRect.width, frameRect.height) independently.
      // CameraFrameOverlay lays the frame out with this same aspect, so the
      // computed rect falls visually on top of the frame border; what the
      // user sees inside the frame is exactly what lands in the export.
      const cssFrameW = frameRect.width;
      const cssFrameH = cssFrameW / targetAspect;
      const cssCx = (frameRect.left + frameRect.right) / 2 - canvasRect.left;
      const cssCy = (frameRect.top + frameRect.bottom) / 2 - canvasRect.top;
      const cssLeft = cssCx - cssFrameW / 2;
      const cssTop = cssCy - cssFrameH / 2;

      // DPR needed so the frame region spans target.width physical pixels.
      const neededDpr = target.width / cssFrameW;

      // Save renderer state
      const savedDpr = gl.getPixelRatio();
      const savedSize = (gl as any).getSize(new THREE.Vector2());

      // Bump DPR while keeping the same logical size → camera stays identical
      gl.setPixelRatio(neededDpr);
      gl.setSize(savedSize.x, savedSize.y, false);

      // Render at high DPR
      renderFn();

      // Crop the frame region from the high-DPR canvas
      const sx = Math.round(cssLeft * neededDpr);
      const sy = Math.round(cssTop * neededDpr);

      if (!exportCanvasRef.current) {
        exportCanvasRef.current = document.createElement("canvas");
      }
      const offscreen = exportCanvasRef.current;
      offscreen.width = target.width;
      offscreen.height = target.height;
      const ctx = offscreen.getContext("2d");
      if (ctx) {
        if (backgroundPreset) {
          drawViewportBgPreset(
            ctx,
            backgroundPreset,
            target.width,
            target.height,
          );
        }
        ctx.drawImage(
          gl.domElement,
          sx,
          sy,
          target.width,
          target.height,
          0,
          0,
          target.width,
          target.height,
        );
      }

      // Restore renderer immediately
      gl.setPixelRatio(savedDpr);
      gl.setSize(savedSize.x, savedSize.y, false);

      window.__poseref_exportCanvas = offscreen;
    },
    [gl],
  );

  // --- Standard export -------------------------------------------------------

  const renderForExport = useCallback(() => {
    const restore = hideExportExclusions(null, true);
    const state = useStore.getState();
    const { renderTransparentBackground } = state;
    const viewportBgPreset = selectActiveRenderConfig(state).viewportBgPreset;
    renderAtTargetRes(
      () => {
        const savedBg = scene.background;
        scene.background = null;
        gl.setClearColor(0x000000, 0);
        gl.clear();
        // Route through PostProcessing when active so screen-space inking
        // edges, AO, and bloom all appear in the export.
        const renderScene = window.__poseref_renderScene;
        if (renderScene) {
          renderScene();
        } else {
          gl.render(scene, camera);
        }
        scene.background = savedBg;
      },
      {
        backgroundPreset: renderTransparentBackground ? null : viewportBgPreset,
      },
    );
    restore();
  }, [gl, scene, camera, hideExportExclusions, renderAtTargetRes]);

  // Tag the viewport canvas once so thumbnail/export code can identify it
  // reliably without a bare `document.querySelector("canvas")` that risks
  // grabbing a CubeSpinner or other unrelated canvas in the DOM.
  useEffect(() => {
    gl.domElement.dataset.viewportCanvas = "1";
    return () => {
      delete gl.domElement.dataset.viewportCanvas;
    };
  }, [gl]);

  /**
   * Render a clean frame and return a square WebP thumbnail blob.
   *
   * Owns the full render-and-capture pipeline so it can read from
   * gl.domElement directly (no DOM query, no wrong-canvas risk).
   *
   * When `isolateObjectIds` is provided, only those scene objects (plus
   * any lights) are rendered — used by library save to produce a clean
   * preview of the asset without unrelated scene clutter.
   */
  const captureThumbnail = useCallback(
    async (
      size = 256,
      isolateObjectIds?: ReadonlySet<string> | null,
    ): Promise<Blob | null> => {
      const restore = hideExportExclusions(isolateObjectIds);
      const savedBg = scene.background;
      // Save/restore the WebGL clear color so the live viewport's CSS gradient
      // is not permanently hidden after thumbnail capture. Without this,
      // setClearColor(bgColor, 1) below leaks into the frame loop and makes the
      // canvas opaque, blocking the radial gradient rendered behind it in CSS.
      const savedClearColor = new THREE.Color();
      gl.getClearColor(savedClearColor);
      const savedClearAlpha = gl.getClearAlpha();
      const viewportBgPreset = selectActiveRenderConfig(
        useStore.getState(),
      ).viewportBgPreset;
      scene.background = null;
      gl.setClearColor(0x000000, 0);
      gl.clear();
      // Route through PostProcessing when active (same as renderForExport)
      const renderScene = window.__poseref_renderScene;
      if (renderScene) {
        renderScene();
      } else {
        gl.render(scene, camera);
      }
      scene.background = savedBg;
      gl.setClearColor(savedClearColor, savedClearAlpha);
      restore();

      // Read from gl.domElement immediately — same synchronous context as the
      // render call, before the browser has a chance to present/swap the buffer.
      return blobFromCanvasCenter(gl.domElement, size, viewportBgPreset);
    },
    [gl, scene, camera, hideExportExclusions],
  );

  window.__poseref_renderForExport = renderForExport;
  window.__poseref_captureThumbnail = captureThumbnail;

  return null;
}

/**
 * Keeps the shared inking material uniforms in sync with the store.
 * Mounted once inside the Canvas tree alongside ExportHelper / FrameStats.
 */
function InkingSync() {
  const thicknessMax = useStore((s) => s.inkingThickness);
  const thicknessMin = useStore((s) => s.inkingThicknessMin);
  const shadowResponse = useStore((s) => s.inkingShadowResponse);
  const shadowExposure = useStore((s) => s.inkingShadowExposure);
  const variation = useStore((s) => s.inkingLineWidthVariation);
  const detail = useStore((s) => s.inkingDetail);
  const distanceNear = useStore((s) => s.inkingDistanceNear);
  const distanceFar = useStore((s) => s.inkingDistanceFar);
  const color = useStore((s) => s.inkingColor);
  const opacity = useStore((s) => s.inkingOpacity);

  useEffect(() => {
    syncInkingUniforms(
      thicknessMax,
      thicknessMin,
      variation,
      detail,
      shadowResponse,
      shadowExposure,
      distanceNear,
      distanceFar,
      color,
      opacity,
    );
  }, [
    thicknessMax,
    thicknessMin,
    variation,
    detail,
    shadowResponse,
    shadowExposure,
    distanceNear,
    distanceFar,
    color,
    opacity,
  ]);

  return null;
}

/**
 * Tracks frame timing, renderer stats, and resource metrics, exposing them
 * on a global so the DebugOverlay (outside the Canvas) can read them.
 */

function FrameStats() {
  const { gl } = useThree();
  const times = useRef<number[]>([]);
  const cpuTimes = useRef<number[]>([]);
  const last = useRef(performance.now());
  const gpuRenderer = useRef<string>("");

  // Read GPU renderer string once from the WebGPU adapter
  if (!gpuRenderer.current) {
    try {
      const adapter = (gl as any).backend?.adapter;
      const info = adapter?.info;
      gpuRenderer.current =
        info?.device || info?.description || info?.vendor || "WebGPU";
    } catch {
      gpuRenderer.current = "Unknown";
    }
  }

  useFrame(() => {
    const frameStart = performance.now();
    const dt = frameStart - last.current;
    last.current = frameStart;

    const arr = times.current;
    arr.push(dt);
    if (arr.length > 120) arr.shift();

    const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
    const render = gl.info.render as typeof gl.info.render & {
      drawCalls?: number;
    };
    const mem = gl.info.memory;

    // JS heap (Chromium only)
    const perfMem = (performance as any).memory;
    const jsHeapUsed = perfMem ? perfMem.usedJSHeapSize : undefined;
    const jsHeapTotal = perfMem ? perfMem.totalJSHeapSize : undefined;
    const jsHeapLimit = perfMem ? perfMem.jsHeapSizeLimit : undefined;

    // Measure CPU time spent in the rest of this frame's JS work
    // (captured at the end of the useFrame callback)
    const cpuArr = cpuTimes.current;

    // Push previous frame's cpu time (we measure at end of callback)
    const frameEnd = performance.now();
    const cpuTime = frameEnd - frameStart;
    cpuArr.push(cpuTime);
    if (cpuArr.length > 120) cpuArr.shift();
    const avgCpu = cpuArr.reduce((a, b) => a + b, 0) / cpuArr.length;

    window.__poseref_frameStats = {
      fps: 1000 / avg,
      frameTime: avg,
      cpuFrameTime: avgCpu,
      drawCalls: render.drawCalls ?? render.calls,
      renderCalls: render.calls,
      triangles: render.triangles,
      geometries: mem.geometries,
      textures: mem.textures,
      gpuRenderer: gpuRenderer.current,
      jsHeapUsed,
      jsHeapTotal,
      jsHeapLimit,
    };
  });

  return null;
}

function classifyCanvasPointerInvalidateReason(
  state: Pick<AppState, "mode" | "editMode" | "commandBuffer">,
): InvalidateReason {
  if (state.mode === "edit" && state.editMode) {
    return state.commandBuffer
      ? "canvasPointerMoveEditCommand"
      : "canvasPointerMoveEditIdle";
  }
  if (state.mode === "transform") return "canvasPointerMoveTransform";
  if (state.mode === "navigate") return "canvasPointerMoveNavigate";
  return "canvasPointerMove";
}

function didEditSelectionChange(
  next: AppState["editMode"],
  prev: AppState["editMode"],
): boolean {
  if (next === prev || !next || !prev) return false;
  return (
    next.selectedVertices !== prev.selectedVertices ||
    next.selectedEdges !== prev.selectedEdges ||
    next.selectedFaces !== prev.selectedFaces ||
    next.hiddenVertices !== prev.hiddenVertices ||
    next.hiddenFaces !== prev.hiddenFaces ||
    next.lastActiveVertex !== prev.lastActiveVertex ||
    next.lastActiveEdge !== prev.lastActiveEdge ||
    next.lastActiveFace !== prev.lastActiveFace
  );
}

function didEditStateChange(
  next: AppState["editMode"],
  prev: AppState["editMode"],
): boolean {
  if (next === prev) return false;
  if (!next || !prev) return next !== prev;
  return (
    next.objectId !== prev.objectId ||
    next.granularity !== prev.granularity ||
    next.curveEditVertices !== prev.curveEditVertices ||
    next.isCurve !== prev.isCurve ||
    next.xray !== prev.xray ||
    next.extrudePending !== prev.extrudePending ||
    next.activeVertexGroupId !== prev.activeVertexGroupId
  );
}

function didCameraStateChange(next: AppState, prev: AppState): boolean {
  return (
    next.cameraProjection !== prev.cameraProjection ||
    next.cameraPosition !== prev.cameraPosition ||
    next.cameraTarget !== prev.cameraTarget ||
    next.cameraUp !== prev.cameraUp ||
    next.focalLength !== prev.focalLength ||
    next.orthographicHalfHeight !== prev.orthographicHalfHeight ||
    next.viewLock !== prev.viewLock ||
    next.flyMode !== prev.flyMode ||
    next.orbitEnabled !== prev.orbitEnabled ||
    next.spaceOrbitActive !== prev.spaceOrbitActive
  );
}

function isEdgeLoopHoverPreviewChange(
  next: AppState["commandBuffer"],
  prev: AppState["commandBuffer"],
): boolean {
  if (!next || !prev) return false;
  if (next.type !== "edgeLoopInsert" || prev.type !== "edgeLoopInsert") {
    return false;
  }
  return (
    next.type === prev.type &&
    next.axis === prev.axis &&
    next.numericInput === prev.numericInput &&
    next.mouseOrigin === prev.mouseOrigin &&
    next.normalVector === prev.normalVector &&
    next.boneLocalAxis === prev.boneLocalAxis &&
    next.preEditSelection === prev.preEditSelection &&
    next.preEditEdges === prev.preEditEdges &&
    next.preEditFaces === prev.preEditFaces &&
    next.preEditActiveVertex === prev.preEditActiveVertex &&
    next.preEditActiveEdge === prev.preEditActiveEdge &&
    next.preEditActiveFace === prev.preEditActiveFace &&
    next.edgeSlideRails === prev.edgeSlideRails &&
    next.gizmoInitiated === prev.gizmoInitiated &&
    next.edgeLoopInsertEdge === prev.edgeLoopInsertEdge &&
    next.boneCommandData === prev.boneCommandData &&
    next.proportionalWeights === prev.proportionalWeights &&
    next.editPivot === prev.editPivot &&
    next.editSelectionVertices === prev.editSelectionVertices &&
    next.preEditSelectionIdentity === prev.preEditSelectionIdentity &&
    next.activeValueMode === prev.activeValueMode &&
    next.individualOriginsComponents === prev.individualOriginsComponents &&
    next.individualOriginsProportionalPivots ===
      prev.individualOriginsProportionalPivots &&
    next.dependentRebuildTargets === prev.dependentRebuildTargets &&
    next.topologyPreviewSeed === prev.topologyPreviewSeed &&
    next.topologyPreviewLastAppliedAt === prev.topologyPreviewLastAppliedAt &&
    next.params === prev.params &&
    (next.edgeLoopHoverEdge !== prev.edgeLoopHoverEdge ||
      next.edgeLoopPreview !== prev.edgeLoopPreview)
  );
}

function classifyStoreInvalidateReason(
  next: AppState,
  prev: AppState,
): InvalidateReason {
  if (next.commandBuffer !== prev.commandBuffer) {
    return isEdgeLoopHoverPreviewChange(next.commandBuffer, prev.commandBuffer)
      ? "storeEditHover"
      : "storeCommand";
  }
  if (next.sceneObjects !== prev.sceneObjects) return "storeScene";
  if (next.materials !== prev.materials) return "storeScene";
  if (next.collections !== prev.collections) return "storeScene";
  if (next.visibility !== prev.visibility) return "storeScene";
  if (next.modifierOverrides !== prev.modifierOverrides) return "storeScene";
  if (next.selectedObjectTargets !== prev.selectedObjectTargets) {
    return "storeScene";
  }
  if (didCameraStateChange(next, prev)) return "storeCamera";
  if (next.cursor3D !== prev.cursor3D) return "storeCursor";
  if (didEditSelectionChange(next.editMode, prev.editMode)) {
    return "storeEditSelection";
  }
  if (didEditStateChange(next.editMode, prev.editMode)) {
    return "storeEditState";
  }
  return "storeUi";
}

/**
 * One-time GPU capability initialization.
 * Resolves max anisotropy and sets up shading diagnostics once the renderer
 * is available, rather than on every MannequinRenderer render.
 */
function RendererInit() {
  const { gl } = useThree();
  useMemo(() => {
    initMaxAnisotropy(gl as unknown as THREE.WebGPURenderer);
    initShadingDiag(useStore);
  }, [gl]);
  return null;
}

/**
 * Wires up on-demand rendering for the non-PT viewport:
 * - Subscribes to the Zustand store so any visual state change (bone poses,
 *   transforms, selection, materials, view mode, etc.) triggers a frame.
 * - Listens for pointermove on the canvas so hover effects and cursor gizmos
 *   stay responsive — R3F raycasting only runs during rendered frames.
 * - Listens for pointermove and keydown on window to kick-start the render
 *   loop during fly mode: pointer lock routes pointermove to document (not
 *   the canvas), and camera move/zoom hold-keys are not store mutations, so
 *   neither would otherwise trigger a frame. The self-sustaining invalidate()
 *   chain in CameraMovement's useFrame takes over once the first frame is
 *   scheduled.
 */
function ViewportInvalidator() {
  const { gl, invalidate } = useThree();
  useEffect(() => {
    let rafId: number | null = null;
    const pendingReasons = new Set<InvalidateReason>();
    const scheduleInvalidate = (reason: InvalidateReason) => {
      pendingReasons.add(reason);
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (pendingReasons.size === 0) return;
        const reasons = Array.from(pendingReasons);
        pendingReasons.clear();
        recordInvalidate(reasons);
        invalidate();
      });
    };
    const inv = (state: AppState, prev: AppState) => {
      // When scene membership changes, prune module-level caches whose
      // keys are scene-object ids. Without this, cached materials and
      // follow-constraint positions accumulate for the lifetime of the
      // session as users add and delete objects.
      if (
        state.sceneObjects !== prev.sceneObjects ||
        state.blendFiles !== prev.blendFiles
      ) {
        const alive = new Set<string>();
        for (const id of state.sceneObjects.keys()) alive.add(id);
        for (const entry of state.blendFiles.values()) {
          for (const obj of entry.file.objects) alive.add(obj.name);
        }
        pruneSelectionPrepassMaterials(alive);
        pruneConstrainedPositions(alive);
      }
      scheduleInvalidate(classifyStoreInvalidateReason(state, prev));
    };
    const unsub = useStore.subscribe(inv);
    const unsubLiveEditPreview = subscribeLiveEditPreview(() => {
      scheduleInvalidate("liveEditPreview");
    });
    const canvas = gl.domElement;
    const invOnCanvasPointerMove = () => {
      const state = useStore.getState();
      if (state.commandBuffer?.mouseOrigin) return;
      scheduleInvalidate(classifyCanvasPointerInvalidateReason(state));
    };
    canvas.addEventListener("pointermove", invOnCanvasPointerMove, {
      passive: true,
    });
    const invIfFlyPointerMove = () => {
      if (!useStore.getState().flyMode) return;
      scheduleInvalidate("windowFlyPointerMove");
    };
    const invIfFlyKeydown = () => {
      if (!useStore.getState().flyMode) return;
      scheduleInvalidate("windowFlyKeydown");
    };
    const invIfCameraKey = (e: KeyboardEvent) => {
      if (
        ZOOM_IN_KEYS.has(e.key) ||
        ZOOM_IN_KEYS.has(e.code) ||
        ZOOM_OUT_KEYS.has(e.key) ||
        ZOOM_OUT_KEYS.has(e.code)
      ) {
        scheduleInvalidate("cameraKey");
        return;
      }
      if (!useStore.getState().flyMode) return;
      if (CAMERA_KEYS.has(e.code) || e.key === "Shift") {
        scheduleInvalidate("cameraKey");
      }
    };
    window.addEventListener("pointermove", invIfFlyPointerMove, {
      passive: true,
    });
    window.addEventListener("keydown", invIfFlyKeydown);
    window.addEventListener("keydown", invIfCameraKey);
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingReasons.clear();
      unsub();
      unsubLiveEditPreview();
      canvas.removeEventListener("pointermove", invOnCanvasPointerMove);
      window.removeEventListener("pointermove", invIfFlyPointerMove);
      window.removeEventListener("keydown", invIfFlyKeydown);
      window.removeEventListener("keydown", invIfCameraKey);
    };
  }, [invalidate, gl]);
  return null;
}

/**
 * CameraRig — owns the complete camera-state lifecycle.
 *
 * This is the one component in the viewport that creates cameras, owns the
 * `OrbitControls` instance, and maps Zustand camera state into three.js in
 * one pass. It replaces a prior split between drei's `<OrbitControls>`,
 * `CameraFovSync`, and `CameraSync` — that split had CameraFovSync swap
 * `useThree().camera` in one React effect while drei rebound
 * `controls.object` in its own effect on the *next* render cycle, so pose
 * writes from axis-view snaps could land on the outgoing camera. Keeping
 * creation, rebind, and pose application inside a single `useLayoutEffect`
 * eliminates that race entirely — the user never observes an intermediate
 * state.
 *
 * Responsibilities:
 *   - Own both the perspective and orthographic three.js cameras.
 *   - Own the `OrbitControls` instance (from three-stdlib, bypassing drei).
 *   - On projection change: copy pose outgoing → incoming, rebind
 *     `controls.object`, publish the active camera to `useThree()`.
 *   - On `cameraPosition` / `cameraTarget` / `cameraUp` change: apply to
 *     the active camera and re-derive the spherical state.
 *   - Each frame: `controls.update()` for damping, then FOV or frustum
 *     sync against `focalLength` / `orthographicHalfHeight`.
 *   - Expose `window.__poseref_camera/_gl/_canvasRect/_orbitControls` for
 *     non-viewport consumers (gizmos, command ops).
 */
function CameraRig({
  controlsRef,
  syncRef,
  orbitScale,
  panScale,
  dollyScale,
  invertOrbit,
  invertPan,
  singleDragPanEnabled,
  onOrbitStart,
  onOrbitChange,
}: {
  controlsRef: React.MutableRefObject<OrbitControlsImpl | null>;
  syncRef: React.MutableRefObject<() => void>;
  orbitScale: number;
  panScale: number;
  dollyScale: number;
  invertOrbit: boolean;
  invertPan: boolean;
  singleDragPanEnabled: boolean;
  onOrbitStart: () => void;
  onOrbitChange: () => void;
}) {
  type LiveCameraState = {
    projection: CameraProjection;
    position: [number, number, number];
    target: [number, number, number];
    up: [number, number, number] | null;
    focalLength: number;
    orthographicHalfHeight: number;
  };
  type OrbitLockedPose = {
    position: [number, number, number];
    target: [number, number, number];
    up: [number, number, number] | null;
  };

  const { gl, camera: defaultCamera, invalidate, set } = useThree();

  const focalLength = useStore((s) => s.focalLength);
  const cameraProjection = useStore((s) => s.cameraProjection);
  const orthographicHalfHeight = useStore((s) => s.orthographicHalfHeight);
  const viewLock = useStore((s) => s.viewLock);

  const perspRef = useRef<THREE.PerspectiveCamera | null>(null);
  const orthoRef = useRef<THREE.OrthographicCamera | null>(null);
  const onOrbitStartRef = useRef(onOrbitStart);
  const onOrbitChangeRef = useRef(onOrbitChange);
  const lastSynced = useRef({
    pos: null as [number, number, number] | null,
    target: null as [number, number, number] | null,
    cameraUp: null as [number, number, number] | null,
    projection: null as CameraProjection | null,
  });
  const viewLockOrbitRef = useRef<{
    stablePose: OrbitLockedPose | null;
    correcting: boolean;
    breaking: boolean;
    navIntent: ViewportNavIntent;
  }>({
    stablePose: null,
    correcting: false,
    breaking: false,
    navIntent: null,
  });

  const fov = useMemo(() => focalLengthToFov(focalLength), [focalLength]);
  onOrbitStartRef.current = onOrbitStart;
  onOrbitChangeRef.current = onOrbitChange;

  function captureLockedPose(): OrbitLockedPose | null {
    const controls = controlsRef.current;
    if (!controls) return null;
    const projection: CameraProjection =
      "isOrthographicCamera" in controls.object &&
      controls.object.isOrthographicCamera
        ? "orthographic"
        : "perspective";
    const pose: OrbitLockedPose = {
      position: controls.object.position.toArray() as [number, number, number],
      target: controls.target.toArray() as [number, number, number],
      up:
        projection === "perspective"
          ? null
          : [controls.object.up.x, controls.object.up.y, controls.object.up.z],
    };
    viewLockOrbitRef.current.stablePose = pose;
    return pose;
  }

  function syncLockedPose(pose: OrbitLockedPose): void {
    const controls = controlsRef.current;
    if (!controls) return;
    controls.object.position.set(...pose.position);
    if (pose.up) controls.object.up.set(...pose.up);
    else controls.object.up.set(0, 0, 1);
    controls.target.set(...pose.target);
    controls.update();
  }

  function handleViewLockOrbitChange(): boolean {
    const state = useStore.getState();
    const lock = state.viewLock;
    const controls = controlsRef.current;
    if (!lock || !controls) return false;

    if (viewLockOrbitRef.current.correcting) {
      viewLockOrbitRef.current.correcting = false;
      return true;
    }
    if (viewLockOrbitRef.current.breaking) {
      return true;
    }
    if (viewLockOrbitRef.current.navIntent !== "rotate") {
      captureLockedPose();
      return false;
    }

    const position = controls.object.position.toArray() as [
      number,
      number,
      number,
    ];
    const target = controls.target.toArray() as [number, number, number];
    const change = classifyViewLockOrbitChange(lock, position, target);
    if (change === "locked") {
      captureLockedPose();
      return false;
    }

    if (change === "orbit-intent") {
      const stablePose =
        viewLockOrbitRef.current.stablePose ?? captureLockedPose();
      if (!stablePose) return false;
      viewLockOrbitRef.current.correcting = true;
      syncLockedPose(stablePose);
      recordInvalidate("viewLockCorrection");
      invalidate();
      return true;
    }

    viewLockOrbitRef.current.stablePose = null;
    viewLockOrbitRef.current.breaking = true;
    state.setCameraOrbit(position, target);
    viewLockOrbitRef.current.breaking = false;
    return true;
  }

  function applyControlsRuntimeConfig(controls: OrbitControlsImpl): void {
    const state = useStore.getState();
    const effectiveOrbit = state.orbitEnabled !== state.spaceOrbitActive;
    const isRenderMode = state.workspaceMode === "render";
    const inkingEditing =
      state.workspaceMode === "inking" &&
      state.inkingMode?.editSubMode === "edit";

    controls.enableRotate = true;
    controls.rotateSpeed = 0.8 * orbitScale * (invertOrbit ? -1 : 1);
    controls.panSpeed = 0.8 * panScale * (invertPan ? -1 : 1);
    controls.zoomSpeed = 1.0 * dollyScale;
    controls.mouseButtons = {
      LEFT: singleDragPanEnabled ? THREE.MOUSE.PAN : THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };
    controls.touches = {
      ONE: singleDragPanEnabled ? THREE.TOUCH.PAN : THREE.TOUCH.ROTATE,
      TWO: THREE.TOUCH.DOLLY_PAN,
    };
    controls.enabled =
      !state.flyMode &&
      state.commandBuffer == null &&
      !state.boxSelectActive &&
      !state.brushSelectActive &&
      state.surfaceTool?.active !== true &&
      !inkingEditing &&
      (effectiveOrbit || isRenderMode);
  }

  function createControls(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  ): OrbitControlsImpl {
    const controls = new OrbitControlsImpl(camera, gl.domElement);
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.minDistance = 0.1;
    controls.maxDistance = 100;
    controls.minPolarAngle = 0.001;
    controls.maxPolarAngle = Math.PI - 0.001;

    const startFn = () => {
      if (useStore.getState().viewLock) captureLockedPose();
      onOrbitStartRef.current();
    };
    const changeFn = () => {
      recordInvalidate("orbitControlsChange");
      invalidate();
      if (handleViewLockOrbitChange()) return;
      onOrbitChangeRef.current();
    };
    controls.addEventListener("start", startFn);
    controls.addEventListener("change", changeFn);
    (
      controls as OrbitControlsImpl & {
        __poseRefStartHandler?: () => void;
        __poseRefChangeHandler?: () => void;
      }
    ).__poseRefStartHandler = startFn;
    (
      controls as OrbitControlsImpl & {
        __poseRefStartHandler?: () => void;
        __poseRefChangeHandler?: () => void;
      }
    ).__poseRefChangeHandler = changeFn;

    applyControlsRuntimeConfig(controls);
    return controls;
  }

  function destroyControls(
    controls: OrbitControlsImpl | null | undefined,
  ): void {
    if (!controls) return;
    const managed = controls as OrbitControlsImpl & {
      __poseRefStartHandler?: () => void;
      __poseRefChangeHandler?: () => void;
    };
    if (managed.__poseRefStartHandler) {
      controls.removeEventListener("start", managed.__poseRefStartHandler);
    }
    if (managed.__poseRefChangeHandler) {
      controls.removeEventListener("change", managed.__poseRefChangeHandler);
    }
    controls.dispose();
  }

  function applyProjectionMatrix(
    projection: CameraProjection,
    focalLengthMm: number,
    halfHeight: number,
  ): void {
    if (projection === "perspective") {
      const cam = perspRef.current;
      if (!cam) return;
      if (cam.view?.enabled) cam.clearViewOffset();
      cam.fov = focalLengthToFov(focalLengthMm);
      cam.updateProjectionMatrix();
      return;
    }
    const cam = orthoRef.current;
    if (!cam) return;
    const aspect = gl.domElement.clientWidth / gl.domElement.clientHeight;
    const halfW = halfHeight * aspect;
    cam.left = -halfW;
    cam.right = halfW;
    cam.top = halfHeight;
    cam.bottom = -halfHeight;
    cam.updateProjectionMatrix();
  }

  function getLiveState(): LiveCameraState | null {
    const controls = controlsRef.current;
    if (!controls) return null;
    const projection: CameraProjection =
      "isOrthographicCamera" in controls.object &&
      controls.object.isOrthographicCamera
        ? "orthographic"
        : "perspective";
    return {
      projection,
      position: controls.object.position.toArray() as [number, number, number],
      target: controls.target.toArray() as [number, number, number],
      up:
        projection === "perspective"
          ? null
          : [controls.object.up.x, controls.object.up.y, controls.object.up.z],
      focalLength: useStore.getState().focalLength,
      orthographicHalfHeight:
        orthoRef.current?.top ?? useStore.getState().orthographicHalfHeight,
    };
  }

  function applyLiveState(
    patch: Partial<LiveCameraState>,
  ): LiveCameraState | null {
    const persp = perspRef.current;
    const ortho = orthoRef.current;
    const controls = controlsRef.current;
    if (!persp || !ortho || !controls) return null;

    const current = getLiveState();
    if (!current) return null;

    const projection = patch.projection ?? current.projection;
    const activeCam = projection === "orthographic" ? ortho : persp;
    const outgoingCam = current.projection === "orthographic" ? ortho : persp;
    const position = patch.position ?? current.position;
    const target = patch.target ?? current.target;
    const up = patch.up !== undefined ? patch.up : current.up;
    const focalLengthMm = patch.focalLength ?? useStore.getState().focalLength;
    const halfHeight =
      patch.orthographicHalfHeight ??
      useStore.getState().orthographicHalfHeight;
    const projectionChanged = projection !== current.projection;

    if (projectionChanged) {
      activeCam.position.copy(outgoingCam.position);
      activeCam.quaternion.copy(outgoingCam.quaternion);
      activeCam.up.copy(outgoingCam.up);
    }

    if (up !== undefined) {
      if (up) activeCam.up.set(up[0], up[1], up[2]);
      else activeCam.up.set(0, 0, 1);
    }
    activeCam.position.set(position[0], position[1], position[2]);
    applyProjectionMatrix(projection, focalLengthMm, halfHeight);

    if (projectionChanged) {
      (
        controls as OrbitControlsImpl & {
          object: THREE.PerspectiveCamera | THREE.OrthographicCamera;
        }
      ).object = activeCam;
      controlsRef.current = controls;
      set({ camera: activeCam });
      window.__poseref_camera = activeCam;
      window.__poseref_orbitControls = controlsRef;
      applyControlsRuntimeConfig(controls);
    }

    controls.target.set(target[0], target[1], target[2]);
    controls.update();
    recordInvalidate("cameraSync");
    invalidate();

    const appliedUp =
      projection === "perspective"
        ? null
        : ([activeCam.up.x, activeCam.up.y, activeCam.up.z] as [
            number,
            number,
            number,
          ]);
    lastSynced.current = {
      pos: position,
      target,
      cameraUp: appliedUp,
      projection,
    };

    return {
      projection,
      position,
      target,
      up: appliedUp,
      focalLength: focalLengthMm,
      orthographicHalfHeight: halfHeight,
    };
  }

  // ─── One-time init ────────────────────────────────────────────────────
  // Runs before the first frame so no frame is ever rendered with
  // unbound controls. We adopt r3f's default perspective camera and
  // lazily create the orthographic one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: OrbitControls owns live camera motion and must only be created on mount.
  useLayoutEffect(() => {
    const persp = (defaultCamera as THREE.PerspectiveCamera).isPerspectiveCamera
      ? (defaultCamera as THREE.PerspectiveCamera)
      : new THREE.PerspectiveCamera(fov, 1, 0.1, 1000);
    const ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 1000);
    ortho.position.copy(persp.position);
    ortho.quaternion.copy(persp.quaternion);
    ortho.up.copy(persp.up);
    perspRef.current = persp;
    orthoRef.current = ortho;

    const initialState = useStore.getState();
    const initialProjection = initialState.cameraProjection;
    const active = initialProjection === "orthographic" ? ortho : persp;
    if (initialState.cameraUp) active.up.set(...initialState.cameraUp);
    else active.up.set(0, 0, 1);
    active.position.set(...initialState.cameraPosition);
    applyProjectionMatrix(
      initialProjection,
      initialState.focalLength,
      initialState.orthographicHalfHeight,
    );

    const controls = createControls(active);
    controlsRef.current = controls;
    controls.target.set(...initialState.cameraTarget);
    controls.update();

    if (active !== defaultCamera) set({ camera: active });
    window.__poseref_orbitControls = controlsRef;
    window.__poseref_camera = active;
    window.__poseref_renderer = gl as unknown as THREE.WebGPURenderer;
    window.__poseref_canvasRect = () => gl.domElement.getBoundingClientRect();

    lastSynced.current = {
      pos: [...initialState.cameraPosition] as [number, number, number],
      target: [...initialState.cameraTarget] as [number, number, number],
      cameraUp: initialState.cameraUp
        ? ([...initialState.cameraUp] as [number, number, number])
        : null,
      projection: initialProjection,
    };

    // Register a live-reader so the sync coordinator can flush the current
    // pose to the store on demand. Reading here also keeps `lastSynced`
    // authoritative whether the write path is debounced or flushed.
    const readLive: LiveCameraRead = () => {
      const c = controlsRef.current;
      if (!c) return null;
      const p = c.object.position.toArray() as [number, number, number];
      const t = c.target.toArray() as [number, number, number];
      // cameraUp is only meaningful in orthographic mode (axis views). In
      // perspective we leave the store's cameraUp untouched — the old
      // value is preserved until the next axis-view switch writes it.
      const isOrtho =
        (c.object as THREE.OrthographicCamera).isOrthographicCamera === true;
      const up: [number, number, number] | null = isOrtho
        ? [c.object.up.x, c.object.up.y, c.object.up.z]
        : null;
      lastSynced.current = {
        pos: p,
        target: t,
        cameraUp: up ?? lastSynced.current.cameraUp,
        projection: lastSynced.current.projection,
      };
      return { position: p, target: t, cameraUp: up };
    };
    const unregisterSync = registerCameraSync(readLive);

    return () => {
      unregisterSync();
      destroyControls(controlsRef.current);
      controlsRef.current = null;
      delete window.__poseref_orbitControls;
      delete window.__poseref_camera;
      delete window.__poseref_cameraController;
      delete window.__poseref_renderer;
      delete window.__poseref_canvasRect;
    };
    // Deliberately mount-scoped: this owns live OrbitControls. If render-
    // created helpers are added as deps, unrelated React renders tear down the
    // controls and rehydrate from the store's last settled camera shadow.
  }, []);

  useLayoutEffect(() => {
    window.__poseref_cameraController = {
      getLiveState,
      applyState: applyLiveState,
    };
  });

  useLayoutEffect(() => {
    viewLockOrbitRef.current.correcting = false;
    viewLockOrbitRef.current.breaking = false;
    viewLockOrbitRef.current.navIntent = null;
    if (viewLock) captureLockedPose();
    else viewLockOrbitRef.current.stablePose = null;
  }, [viewLock, captureLockedPose]);

  useEffect(() => {
    const canvas = gl.domElement;

    function onPointerDown(e: PointerEvent) {
      const controls = controlsRef.current;
      if (!controls) return;
      if (e.pointerType === "mouse") {
        viewLockOrbitRef.current.navIntent = navIntentFromMousePointer(
          e.button,
          { shiftKey: e.shiftKey, metaKey: e.metaKey, ctrlKey: e.ctrlKey },
          controls.mouseButtons,
        );
      }
    }

    function onPointerUp() {
      viewLockOrbitRef.current.navIntent = null;
    }

    function onTouchStart(e: TouchEvent) {
      const controls = controlsRef.current;
      if (!controls) return;
      viewLockOrbitRef.current.navIntent = navIntentFromTouchGesture(
        e.touches.length,
        controls.touches,
      );
    }

    function onTouchEnd() {
      viewLockOrbitRef.current.navIntent = null;
    }

    canvas.addEventListener("pointerdown", onPointerDown, true);
    canvas.addEventListener("pointerup", onPointerUp, true);
    canvas.addEventListener("pointercancel", onPointerUp, true);
    canvas.addEventListener("touchstart", onTouchStart, true);
    canvas.addEventListener("touchend", onTouchEnd, true);
    canvas.addEventListener("touchcancel", onTouchEnd, true);
    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown, true);
      canvas.removeEventListener("pointerup", onPointerUp, true);
      canvas.removeEventListener("pointercancel", onPointerUp, true);
      canvas.removeEventListener("touchstart", onTouchStart, true);
      canvas.removeEventListener("touchend", onTouchEnd, true);
      canvas.removeEventListener("touchcancel", onTouchEnd, true);
    };
  }, [gl, controlsRef]);

  // ─── Prop-derived control config ──────────────────────────────────────
  // Speeds, button mappings, viewLock gating. No three.js state
  // mutation that would require a frame; pure property assignment.
  useLayoutEffect(() => {
    const c = controlsRef.current;
    if (!c) return;
    applyControlsRuntimeConfig(c);
  }, [controlsRef, applyControlsRuntimeConfig]);

  // ─── Per-frame ticks ──────────────────────────────────────────────────
  useFrame(() => {
    // `update()` drives damping — run it every frame even when disabled so
    // residual damping decays cleanly after a gesture ends or controls are
    // toggled off mid-drag. The event handlers themselves guard on
    // `enabled`, so no user input sneaks through.
    controlsRef.current?.update();

    if (cameraProjection === "perspective") {
      const cam = perspRef.current;
      if (!cam) return;
      if (Math.abs(cam.fov - fov) > 0.01) {
        applyProjectionMatrix(
          "perspective",
          focalLength,
          orthographicHalfHeight,
        );
      }
    } else {
      const cam = orthoRef.current;
      if (!cam) return;
      const aspect = gl.domElement.clientWidth / gl.domElement.clientHeight;
      const halfW = orthographicHalfHeight * aspect;
      if (
        Math.abs(cam.top - orthographicHalfHeight) > 0.001 ||
        Math.abs(cam.right - halfW) > 0.001
      ) {
        applyProjectionMatrix(
          "orthographic",
          focalLength,
          orthographicHalfHeight,
        );
      }
    }
  });

  // Controls → store. Delegates to the sync coordinator so every write path
  // (orbit debounce, fly-mode settle, transition completion, mode-switch
  // flush) converges on a single, atomic live → shadow update.
  syncRef.current = flushCameraSync;

  return null;
}

/**
 * Disables orbit controls while a command buffer is active (G/R/S mouse
 * transforms), while orbitEnabled is false, or while Apple Pencil is in use.
 *
 * When `orbitEnabled` is toggled off in the toolbar, ALL pointer-driven camera
 * movement is disabled (mouse orbit/pan/zoom, single-finger touch).
 * Multi-finger touch gestures (2-finger orbit+dolly, 3-finger pan) are handled
 * independently by MultiTouchHandler and remain available regardless.
 *
 * In Render mode the camera is always freely movable regardless of the orbit
 * toggle so the user can frame the shot without switching modes.
 */
function OrbitControlsGuard({
  controlsRef,
}: {
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
}) {
  const commandBuffer = useStore((s) => s.commandBuffer);
  const orbitEnabled = useStore((s) => s.orbitEnabled);
  const spaceOrbitActive = useStore((s) => s.spaceOrbitActive);
  const boxSelectActive = useStore((s) => s.boxSelectActive);
  const brushSelectActive = useStore((s) => s.brushSelectActive);
  const surfaceToolActive = useStore((s) => s.surfaceTool?.active === true);
  const isRenderMode = useStore((s) => s.workspaceMode === "render");
  // Inking edit sub-mode freezes the 3D camera so baked strokes stay aligned
  // with the geometry beneath them. Read as a boolean rather than pulling
  // inkingMode so the effect only fires on transitions into/out of edit.
  const inkingEditing = useStore(
    (s) => s.workspaceMode === "inking" && s.inkingMode?.editSubMode === "edit",
  );
  const invertZoom = useStore((s) => s.invertZoom);
  const flyMode = useStore((s) => s.flyMode);
  const { gl } = useThree();

  // Single source of truth for controls.enabled.  Space-hold inverts the
  // orbit toggle (XOR), render mode always allows orbit, and active G/R/S
  // commands disable orbit regardless. Selection modals suppress orbit too.
  // Fly mode disables orbit so mouse look can drive the camera directly.
  //
  // `enableRotate` — separate from `enabled` — is driven directly by the
  // drei <OrbitControls enableRotate={...}> prop, bound to `viewLock`. An
  // imperative mutation here would be clobbered on the next render because
  // drei re-syncs its declarative props every time.
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    const effectiveOrbit = orbitEnabled !== spaceOrbitActive;
    controls.enabled =
      !flyMode &&
      commandBuffer == null &&
      !boxSelectActive &&
      !brushSelectActive &&
      !surfaceToolActive &&
      !inkingEditing &&
      (effectiveOrbit || isRenderMode);
  }, [
    commandBuffer,
    orbitEnabled,
    spaceOrbitActive,
    boxSelectActive,
    brushSelectActive,
    surfaceToolActive,
    isRenderMode,
    inkingEditing,
    flyMode,
    controlsRef,
  ]);

  // Wheel-zoom handling. Two reasons to intercept OrbitControls' built-in
  // zoom: (1) invertZoom, which flips the direction; (2) orthographic
  // projection, where we want wheel to scale the store's ortho half-height
  // rather than dolly the camera along the view direction — this is what
  // Blender numpad-ortho does and is what users expect from an axis view.
  //
  // When either reason applies we take over the wheel events: set
  // `enableZoom = false` and install our own listener.
  const cameraProjection = useStore((s) => s.cameraProjection);
  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    const ortho = cameraProjection === "orthographic";
    if (!invertZoom && !ortho) return;
    controls.enableZoom = false;

    const canvas = gl.domElement;
    const handler = (e: WheelEvent) => {
      if (!controls.enabled) return;
      e.preventDefault();
      const state = useStore.getState();
      if (state.cameraProjection === "orthographic") {
        // Factor < 1 shrinks frustum (zoom in), > 1 expands (zoom out).
        const step = 0.95 ** (Math.abs(e.deltaY * 0.01) * controls.zoomSpeed);
        const sign = invertZoom ? -1 : 1;
        const factor = e.deltaY * sign < 0 ? step : 1 / step;
        state.setOrthographicHalfHeight(state.orthographicHalfHeight * factor);
        return;
      }
      // Perspective + invertZoom: hand-rolled inverted dolly.
      const scale = 0.95 ** (Math.abs(e.deltaY * 0.01) * controls.zoomSpeed);
      if (e.deltaY < 0) controls.dollyOut(scale);
      else if (e.deltaY > 0) controls.dollyIn(scale);
    };
    canvas.addEventListener("wheel", handler, { passive: false });
    return () => {
      canvas.removeEventListener("wheel", handler);
      if (controlsRef.current) controlsRef.current.enableZoom = true;
    };
  }, [gl, controlsRef, invertZoom, cameraProjection]);

  // Space key — hold to temporarily invert the orbit toggle; tap to
  // permanently toggle it.  Skipped in render mode (camera always free)
  // and when the refImage transform mode owns Space.
  //
  // A "tap" is a press+release shorter than TAP_THRESHOLD_MS with no
  // significant pointer movement in between (future-proof).
  useEffect(() => {
    const TAP_THRESHOLD_MS = 200;
    let spaceHeld = false;
    let downTime = 0;

    const isSpace = (e: KeyboardEvent) => e.code === "Space" || e.key === " ";

    function onKeyDown(e: KeyboardEvent) {
      if (!isSpace(e) || e.repeat || isEditableField(e)) return;
      const s = useStore.getState();
      if (s.workspaceMode === "render") return;
      if (s.mode === "transform" && s.transformMode === "refImage") return;
      if (s.commandBuffer != null) return;
      e.preventDefault();
      spaceHeld = true;
      downTime = performance.now();
      s.setSpaceOrbitActive(true);
    }

    function onKeyUp(e: KeyboardEvent) {
      if (!isSpace(e) || !spaceHeld) return;
      const elapsed = performance.now() - downTime;
      spaceHeld = false;
      const s = useStore.getState();
      if (s.spaceOrbitActive) s.setSpaceOrbitActive(false);
      if (elapsed < TAP_THRESHOLD_MS) {
        s.setOrbitEnabled(!s.orbitEnabled);
      }
    }

    // Reset on blur (e.g. user Alt-Tabs while holding Space).
    function onBlur() {
      if (!spaceHeld) return;
      spaceHeld = false;
      const s = useStore.getState();
      if (s.spaceOrbitActive) s.setSpaceOrbitActive(false);
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // Also disable orbit while a camera transition is animating.
  useFrame(({ camera }) => {
    const controls = controlsRef.current;
    if (controls && isCameraTransitioning()) {
      controls.enabled = false;
    }

    // Update gridLogDist, which drives the grid material's LOD. The metric
    // has to mean "how many world units does the visible frame cover" so
    // that fine/standard/coarse grid levels switch at the right zoom:
    //
    //  - Perspective: the effective visible scale is a blend of the
    //    distance to the grid plane (matters for grazing views) and the
    //    camera height (matters at the horizon, where the floor distance
    //    blows up). Matches Blender's heuristic.
    //  - Orthographic: there is no distance-driven frame — the ortho
    //    half-height IS the visible world extent, so drive LOD directly
    //    from it. Without this branch, axis views (Front/Left/etc.) see
    //    `dir.z ≈ 0` and the perspective formula degenerates.
    const state = useStore.getState();
    let logDist: number;
    if (state.cameraProjection === "orthographic") {
      logDist = Math.log10(Math.max(0.01, state.orthographicHalfHeight));
    } else {
      const pos = camera.position;
      const dir = camera.getWorldDirection(new THREE.Vector3());
      const distFloor = Math.abs(pos.z / dir.z);
      const distVertical = Math.abs(pos.z);
      const weight = 1.0 - Math.abs(dir.z);
      const dist = distFloor * (1.0 - weight) + distVertical * weight;
      const safeDist = Math.max(
        0.01,
        Number.isFinite(dist) ? dist : distVertical,
      );
      logDist = Math.log10(safeDist);
    }

    state.setGridLogDist(logDist);
  });

  // Prevent Apple Pencil (pen) from driving OrbitControls.
  // On pointerdown we disable controls so OrbitControls ignores the event;
  // on pointerup we re-enable. This is scoped to pen only — finger, mouse,
  // and trackpad orbit normally. Other systems (R3F events, SculptHandler)
  // are unaffected because we don't stop propagation.
  //
  // This effect also manages the _suppressRaycast flag that prevents R3F from
  // performing expensive scene-wide raycasts during navigation gestures.
  // On pointerdown we always suppress (navigation is the common case); on
  // pointerup we only allow the raycast through when the pointer barely moved
  // (a tap intended for object selection).
  useEffect(() => {
    const canvas = gl.domElement;
    let penActive = false;

    function onPenDown(e: PointerEvent) {
      // Disable orbit for pen input (Apple Pencil).
      if (e.pointerType !== "pen") return;
      const controls = controlsRef.current;
      if (!controls) return;
      penActive = true;
      controls.enabled = false;
    }

    function onPenUp(e: PointerEvent) {
      if (!penActive) return;
      if (e.pointerType !== "pen") return;
      const s = useStore.getState();
      penActive = false;
      const controls = controlsRef.current;
      if (!controls) return;
      // Re-enable only if nothing else is disabling it.
      const effectiveOrbit = s.orbitEnabled !== s.spaceOrbitActive;
      controls.enabled =
        s.commandBuffer == null &&
        (effectiveOrbit || s.workspaceMode === "render");
    }

    // --- Navigation raycast suppression ---
    // Suppress raycasting only during active navigation drags (orbit/pan/dolly).
    // pointerdown and pointerup always allow raycasting so that tap-to-select
    // works reliably — BVH acceleration makes down/up raycasts fast (O(log n)).
    // We only suppress pointermove raycasts once the pointer has moved beyond
    // the tap threshold, avoiding continuous intersection tests during drags.
    function onNavDown(e: PointerEvent) {
      _navDownPos = { x: e.clientX, y: e.clientY };
      // Don't suppress here — let onPointerDown reach mesh handlers for tap detection
    }

    function onNavMove(e: PointerEvent) {
      if (!_navDownPos) return;
      const dx = e.clientX - _navDownPos.x;
      const dy = e.clientY - _navDownPos.y;
      if (dx * dx + dy * dy > NAV_TAP_THRESHOLD_SQ) {
        // Pointer moved beyond tap threshold — this is a drag, suppress raycasts
        _suppressRaycast = true;

        // If orbit is disabled because the user toggled it off (not because
        // of an active command), flash the navigate button as a hint.
        const s = useStore.getState();
        const effectiveOrbit = s.orbitEnabled !== s.spaceOrbitActive;
        if (
          !effectiveOrbit &&
          !s.commandBuffer &&
          s.workspaceMode !== "render"
        ) {
          window.dispatchEvent(new Event("nav-blocked"));
          _navDownPos = null; // Prevent repeated dispatches during same drag
        }
      }
    }

    // Wheel events when orbit is disabled — flash the navigate button.
    // Debounced so rapid scroll ticks don't spam the event.
    let wheelBlockedAt = 0;
    function onWheel() {
      const s = useStore.getState();
      const effectiveOrbit = s.orbitEnabled !== s.spaceOrbitActive;
      if (!effectiveOrbit && !s.commandBuffer && s.workspaceMode !== "render") {
        const now = performance.now();
        if (now - wheelBlockedAt > 600) {
          wheelBlockedAt = now;
          window.dispatchEvent(new Event("nav-blocked"));
        }
      }
    }

    function onNavUp(_e: PointerEvent) {
      _navDownPos = null;
      _suppressRaycast = false;
    }

    canvas.addEventListener("pointerdown", onPenDown, true);
    canvas.addEventListener("pointerup", onPenUp, true);
    canvas.addEventListener("pointercancel", onPenUp, true);

    canvas.addEventListener("pointerdown", onNavDown, true);
    canvas.addEventListener("pointermove", onNavMove, true);
    canvas.addEventListener("pointerup", onNavUp, true);
    canvas.addEventListener("pointercancel", onNavUp, true);
    canvas.addEventListener("wheel", onWheel, true);

    return () => {
      canvas.removeEventListener("pointerdown", onPenDown, true);
      canvas.removeEventListener("pointerup", onPenUp, true);
      canvas.removeEventListener("pointercancel", onPenUp, true);

      canvas.removeEventListener("pointerdown", onNavDown, true);
      canvas.removeEventListener("pointermove", onNavMove, true);
      canvas.removeEventListener("pointerup", onNavUp, true);
      canvas.removeEventListener("pointercancel", onNavUp, true);
      canvas.removeEventListener("wheel", onWheel, true);
    };
  }, [gl, controlsRef]);

  return null;
}

/** Camera movement speed in units/second */
const CAMERA_SPEED = 0.8;
/** Speed multiplier when Shift is held during fly mode */
const FLY_SHIFT_MULTIPLIER = 5;
/** Mouse look sensitivity (radians per pixel) */
const FLY_LOOK_SENSITIVITY = 0.002;

/**
 * Reads camera movement keys (WASD/QE in fly mode, +/-) each frame and moves
 * both the camera and the OrbitControls target together.
 *
 * W/S — forward/backward  |  A/D — left/right  |  Q/E — down/up
 * zoomIn/zoomOut — dolly toward/away from orbit target
 *
 * In fly mode, mouse movement rotates the camera (yaw/pitch) and Shift
 * multiplies movement speed.
 *
 * Syncs back to store when all keys are released.
 */
function CameraMovement({
  cameraKeysRef,
  controlsRef,
}: {
  cameraKeysRef: React.RefObject<CameraKeys>;
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
}) {
  const { camera } = useThree();
  const _dir = useRef(new THREE.Vector3());
  const _right = useRef(new THREE.Vector3());
  const _move = useRef(new THREE.Vector3());
  const _wasActive = useRef(false);
  const _yawQuat = useRef(new THREE.Quaternion());
  const _pitchQuat = useRef(new THREE.Quaternion());
  const _fwd = useRef(new THREE.Vector3());

  useFrame((state, delta) => {
    const keys = cameraKeysRef.current;
    const controls = controlsRef.current;
    if (!keys || !controls) return;

    const inFlyMode = useStore.getState().flyMode;

    // ── Fly mode mouse look ──────────────────────────────────
    // Apply incremental quaternion rotations: yaw around world Z,
    // pitch around camera-local right. This avoids Euler decomposition
    // artifacts and allows full spherical rotation like Blender.
    if (inFlyMode && (flyMouseDelta.x !== 0 || flyMouseDelta.y !== 0)) {
      notifyCameraGestureStart();
      const dx = flyMouseDelta.x;
      const dy = flyMouseDelta.y;
      flyMouseDelta.x = 0;
      flyMouseDelta.y = 0;

      // Yaw: rotate around world Z (unrestricted)
      _yawQuat.current.setFromAxisAngle(WORLD_UP, -dx * FLY_LOOK_SENSITIVITY);
      camera.quaternion.premultiply(_yawQuat.current);

      // Pitch: rotate around camera-local right axis, clamped at poles
      camera.getWorldDirection(_fwd.current);
      const currentPitch = Math.asin(
        THREE.MathUtils.clamp(_fwd.current.dot(WORLD_UP), -1, 1),
      );
      const rawPitch = -dy * FLY_LOOK_SENSITIVITY;
      const maxPitch = Math.PI / 2 - 0.01;
      const clampedPitch =
        THREE.MathUtils.clamp(currentPitch + rawPitch, -maxPitch, maxPitch) -
        currentPitch;

      _right.current.set(1, 0, 0).applyQuaternion(camera.quaternion);
      _pitchQuat.current.setFromAxisAngle(_right.current, clampedPitch);
      camera.quaternion.premultiply(_pitchQuat.current);
      camera.quaternion.normalize();

      // Move orbit target to stay at the same distance in front of camera
      const orbitDist = camera.position.distanceTo(controls.target);
      camera.getWorldDirection(_dir.current);
      controls.target
        .copy(camera.position)
        .addScaledVector(_dir.current, orbitDist);

      controls.update();
      recordInvalidate("cameraMovementFrame");
      state.invalidate();
    }

    // ── WASD / zoom key movement ─────────────────────────────
    const hasMovementKeys = inFlyMode
      ? [...keys].some((k) => k !== "flyShift") // ignore shift-only
      : keys.size > 0;

    // Sync to store when all keys released after movement. Debounced via
    // the coordinator so rapid key taps coalesce into a single write.
    if (!hasMovementKeys && _wasActive.current) {
      _wasActive.current = false;
      notifyCameraGestureStart();
      markCameraDirty();
      return;
    }

    if (!hasMovementKeys) return;

    if (!_wasActive.current) {
      notifyCameraGestureStart();
    }
    _wasActive.current = true;

    camera.getWorldDirection(_dir.current);
    _right.current.crossVectors(_dir.current, WORLD_UP).normalize();

    const move = _move.current.set(0, 0, 0);
    if (keys.has("w")) move.add(_dir.current);
    if (keys.has("s")) move.sub(_dir.current);
    if (keys.has("d")) move.add(_right.current);
    if (keys.has("a")) move.sub(_right.current);

    if (move.lengthSq() > 0) move.normalize();

    if (keys.has("e")) move.z += 1;
    if (keys.has("q")) move.z -= 1;

    const speed =
      inFlyMode && keys.has("flyShift")
        ? CAMERA_SPEED * FLY_SHIFT_MULTIPLIER
        : CAMERA_SPEED;
    move.multiplyScalar(speed * delta);
    camera.position.add(move);
    controls.target.add(move);

    // Keyboard zoom: dolly toward/away from orbit target
    if (keys.has("zoomIn") || keys.has("zoomOut")) {
      const dir = keys.has("zoomIn") ? -1 : 1;
      const offset = camera.position.clone().sub(controls.target);
      const factor = 1 + dir * 0.15;
      offset.multiplyScalar(Math.max(factor, 0.01));
      camera.position.copy(controls.target).add(offset);
    }

    controls.update();
    recordInvalidate("cameraMovementFrame");
    state.invalidate();
  });

  return null;
}

/**
 * Exposes a global __poseref_raycastAt(screenX, screenY) that returns the
 * world-space intersection point of the nearest visible mesh under a given
 * screen coordinate, or null if nothing is hit.
 */
function surfaceTriangleFromRaycastHit(
  hit: THREE.Intersection<THREE.Object3D>,
): ViewportSurfaceHit["triangle"] | undefined {
  if (!hit.face || !(hit.object as any).isMesh) return undefined;
  const geometry = (hit.object as THREE.Mesh).geometry;
  const position = geometry?.getAttribute("position");
  if (!position) return undefined;

  const read = (index: number): [number, number, number] =>
    new THREE.Vector3()
      .fromBufferAttribute(position, index)
      .applyMatrix4(hit.object.matrixWorld)
      .toArray() as [number, number, number];

  return {
    vertices: [read(hit.face.a), read(hit.face.b), read(hit.face.c)],
  };
}

interface SurfaceGeometryMetadata {
  poserefSurfaceTopology?: PolygonTopology;
  poserefSurfacePositions?: Float32Array;
  poserefTriToFace?: Uint32Array;
}

function surfaceFaceFromRaycastHit(
  hit: THREE.Intersection<THREE.Object3D>,
): ViewportSurfaceHit["face"] | undefined {
  if (typeof hit.faceIndex !== "number" || !(hit.object as any).isMesh) {
    return undefined;
  }
  const geometry = (hit.object as THREE.Mesh).geometry;
  const metadata = geometry.userData as SurfaceGeometryMetadata;
  const topology = metadata.poserefSurfaceTopology;
  const positions = metadata.poserefSurfacePositions;
  const triToFace = metadata.poserefTriToFace;
  if (!topology || !positions || !triToFace) return undefined;

  const faceIndex = triToFace[hit.faceIndex];
  if (faceIndex === undefined) return undefined;
  const start = topology.offsets[faceIndex];
  const end = topology.offsets[faceIndex + 1];
  if (start === undefined || end === undefined || end <= start) {
    return undefined;
  }

  const vertices: [number, number, number][] = [];
  for (let c = start; c < end; c += 1) {
    const vi = topology.corners[c]!;
    vertices.push(
      new THREE.Vector3(
        positions[vi * 3]!,
        positions[vi * 3 + 1]!,
        positions[vi * 3 + 2]!,
      )
        .applyMatrix4(hit.object.matrixWorld)
        .toArray() as [number, number, number],
    );
  }
  return {
    index: faceIndex,
    vertices,
    mesh: {
      topology,
      positions,
      worldMatrix: hit.object.matrixWorld.elements.slice(),
    },
  };
}

function RaycastHelper() {
  const { scene, camera, gl } = useThree();

  // Cache raycaster + NDC between calls. __poseref_raycastAt is called by
  // external tools (place-on-surface, snap helpers) and was previously
  // allocating a fresh pair every invocation.
  const raycasterRef = useRef<THREE.Raycaster | null>(null);
  const ndcRef = useRef<THREE.Vector2 | null>(null);
  const normalMatrixRef = useRef<THREE.Matrix3 | null>(null);
  if (!raycasterRef.current) raycasterRef.current = new THREE.Raycaster();
  if (!ndcRef.current) ndcRef.current = new THREE.Vector2();
  if (!normalMatrixRef.current) normalMatrixRef.current = new THREE.Matrix3();

  const raycastSurfaceAtNdc = useCallback(
    (
      ndcX: number,
      ndcY: number,
      excludeObjectIds: ReadonlySet<string> = EMPTY_SURFACE_EXCLUDE_IDS,
    ): ViewportSurfaceHit | null => {
      const ndc = ndcRef.current!;
      ndc.set(ndcX, ndcY);
      const raycaster = raycasterRef.current!;
      const normalMatrix = normalMatrixRef.current!;
      raycaster.setFromCamera(ndc, camera);

      const intersections = raycaster.intersectObjects(scene.children, true);
      for (const hit of intersections) {
        if (!hit.object.visible) continue;
        if (!(hit.object as any).isMesh) continue;
        if ((hit.object as any).material?.type === "GridMaterial") continue;
        if ((hit.object as any).material?.visible === false) continue;
        if (hasExcludeTag(hit.object)) continue;
        const objectId = resolveSurfaceObjectId(hit.object);
        if (!objectId || excludeObjectIds.has(objectId)) continue;
        const normal = hit.face?.normal
          ? hit.face.normal
              .clone()
              .applyNormalMatrix(
                normalMatrix.getNormalMatrix(hit.object.matrixWorld),
              )
              .toArray()
          : [0, 0, 1];
        const face = surfaceFaceFromRaycastHit(hit);
        const triangle = surfaceTriangleFromRaycastHit(hit);
        return {
          objectId,
          worldPosition: hit.point.toArray() as [number, number, number],
          normal: normal as [number, number, number],
          ...(face ? { face } : {}),
          ...(triangle ? { triangle } : {}),
        };
      }
      return null;
    },
    [scene, camera],
  );

  const raycastAt = useCallback(
    (
      screenX: number,
      screenY: number,
    ): {
      point: [number, number, number];
      normal: [number, number, number];
    } | null => {
      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();
      const ndc = ndcRef.current!;
      ndc.set(
        ((screenX - rect.left) / rect.width) * 2 - 1,
        -((screenY - rect.top) / rect.height) * 2 + 1,
      );
      const hit = raycastSurfaceAtNdc(ndc.x, ndc.y);
      if (!hit) return null;
      return { point: hit.worldPosition, normal: hit.normal };
    },
    [gl, raycastSurfaceAtNdc],
  );

  useEffect(() => {
    window.__poseref_raycastAt = raycastAt;
    window.__poseref_scene = scene;
    registerViewportSurfaceRaycast(raycastSurfaceAtNdc);
    return () => {
      delete window.__poseref_raycastAt;
      delete window.__poseref_scene;
      registerViewportSurfaceRaycast(null);
    };
  }, [raycastAt, raycastSurfaceAtNdc, scene]);

  return null;
}

/**
 * Main 3D viewport component.
 * Uses Blender-consistent Z-up coordinate system.
 */
export function Viewport3D({
  cameraKeysRef,
  controlsRef,
  cameraSyncRef,
}: {
  cameraKeysRef: React.RefObject<CameraKeys>;
  controlsRef: React.MutableRefObject<OrbitControlsImpl | null>;
  cameraSyncRef: React.MutableRefObject<() => void>;
}) {
  const focalLength = useStore((s) => s.focalLength);
  useActiveRenderConfig((c) => c.viewMode);
  const showSceneInteraction = useStore((s) =>
    viewportSupportsSceneInteraction(s.workspaceMode),
  );
  const orbitScale = useStore((s) => getInputScale("orbit", s));
  const panScale = useStore((s) => getInputScale("pan", s));
  const dollyScale = useStore((s) => getInputScale("dolly", s));
  const invertOrbit = useStore((s) => s.invertOrbit);
  const invertPan = useStore((s) => s.invertPan);
  const singleDragPanEnabled = useStore((s) => s.singleDragPanEnabled);

  const fov = useMemo(() => focalLengthToFov(focalLength), [focalLength]);

  // Adaptive quality — silently scales back rendering when scene is heavy
  const quality = useAdaptivePerformance();

  // Read initial camera position from store (session data is already applied
  // before first render). Wrapped in useRef so re-renders don't reset the Canvas.
  const initialCamera = useRef({
    fov,
    near: 0.1,
    far: 1000,
    position: useStore.getState().cameraPosition,
    up: [0, 0, 1] as [number, number, number],
  }).current;

  // Sculpt mode cursor state (mutable ref for per-frame updates)
  const sculptCursorRef = useRef<BrushCursorState>({
    position: new THREE.Vector3(),
    normal: new THREE.Vector3(0, 0, 1),
    visible: false,
    stroking: false,
    pressure: 0,
  });
  const sculptMode = useStore((s) => s.sculptMode);

  // Sketch mode cursor state (mutable ref for per-frame updates)
  const sketchCursorRef = useRef<BrushCursorState>({
    position: new THREE.Vector3(),
    normal: new THREE.Vector3(0, 0, 1),
    visible: false,
    stroking: false,
    pressure: 0,
  });
  const sketchMode = useStore((s) => s.sketchMode);

  // Debounce "camera settled" off OrbitControls' change events rather than
  // pointerup: in demand mode damping continues after release, and the
  // programmatic zoom paths also bypass OrbitControls' end event entirely.
  // The coordinator in ./cameraSync.ts owns the timer and cancellation.
  const handleOrbitStart = useCallback(() => {
    // Commit any prior gesture's pending write synchronously so the store
    // matches the live camera before the new gesture begins mutating it.
    flushCameraSync();
    // Distinct from change events: this is the user's authoritative "I am
    // starting to manipulate the camera" signal. Subscribers (e.g. inking
    // auto-bake) use it to differentiate real motion from post-settle
    // damping echoes that any store update can wake the render loop into
    // emitting.
    notifyCameraGestureStart();
  }, []);

  const handleOrbitChange = useCallback(() => {
    markCameraDirty();
  }, []);

  // Tap in empty space — deselect
  const handlePointerMissed = useCallback((e: MouseEvent) => {
    if (e.button === 2) return;
    const state = useStore.getState();

    // Eyedropper pick: background click cancels the session without deselecting
    // the current scene selection.
    if (state.eyedropperSession) {
      state.eyedropperSession.onCancel();
      state.endEyedropperSession();
      return;
    }

    if (state.commandBuffer) return;
    if (state.boxSelectActive) return;
    if (state.brushSelectActive) return;
    if (state.surfaceTool?.active === true) return;

    // An empty-space click invalidates any in-progress z-depth cycle: the
    // next object/edit click should start fresh at the frontmost hit rather than
    // advance through a stale stack from a previous click-through.
    resetSelectionCycle();
    resetEditSelectionCycle();

    if (state.editMode) {
      // Don't clear selection when a pending operator is armed — the user
      // is clicking empty space as the intended target (extrude). Clearing
      // here would wipe their selection before the operator fires.
      if (state.editMode.extrudePending) {
        return;
      }
      state.clearVertexSelection();
    } else if (state.poseMode) {
      state.selectBone(null);
    } else if (state.sketchMode || state.sculptMode) {
      // Sketch/sculpt modes are bound to a specific object — don't deselect
      // the target when clicking empty space.
      return;
    } else {
      state.selectObject(null);
    }
  }, []);

  return (
    <Canvas
      shadows={REALTIME_CANVAS_SHADOWS}
      camera={initialCamera}
      frameloop="demand"
      dpr={[1, quality.maxDpr]}
      gl={async (defaultProps) => {
        if (!navigator.gpu) {
          throw new Error(
            "WebGPU is not supported in this browser. " +
              "Please use a recent version of Chrome, Edge, or Firefox with WebGPU enabled.",
          );
        }
        const renderer = new THREE.WebGPURenderer({
          canvas: defaultProps.canvas as HTMLCanvasElement,
          antialias: true,
          alpha: true,
        });
        await renderer.init();
        applyDisplayColorPipeline(renderer);
        return renderer as any;
      }}
      onCreated={(state) => {
        // Skip raycasting during active navigation drags and filter out
        // invisible objects and their descendants.
        state.events.filter = (items) => {
          if (_suppressRaycast) return [];
          return items.filter((hit) => {
            let obj: THREE.Object3D | null = hit.object;
            while (obj) {
              if (!obj.visible) return false;
              obj = obj.parent;
            }
            return true;
          });
        };
        // Dismiss the loading overlay now that the canvas is ready to render.
        // Deferred one frame so the first frame can land before the overlay
        // fades out, avoiding a flash of empty viewport.
        requestAnimationFrame(() => {
          useStore.setState({ loading: false });
        });
      }}
      style={{
        position: "relative",
        zIndex: 2,
        width: "100%",
        height: "100%",
      }}
      onPointerMissed={handlePointerMissed}
    >
      <RendererInit />
      <ViewportInvalidator />
      <CameraRig
        controlsRef={controlsRef}
        syncRef={cameraSyncRef}
        orbitScale={orbitScale}
        panScale={panScale}
        dollyScale={dollyScale}
        invertOrbit={invertOrbit}
        invertPan={invertPan}
        singleDragPanEnabled={singleDragPanEnabled}
        onOrbitStart={handleOrbitStart}
        onOrbitChange={handleOrbitChange}
      />
      <CameraTransitionDriver controlsRef={controlsRef} />
      <FrameStats />
      <ExportHelper />
      <InkingSync />
      <RaycastHelper />

      <CameraMovement cameraKeysRef={cameraKeysRef} controlsRef={controlsRef} />

      <OrbitControlsGuard controlsRef={controlsRef} />
      <MultiTouchHandler controlsRef={controlsRef} syncRef={cameraSyncRef} />

      <SceneLights />
      <SceneEnvironment />
      <BackgroundImage />
      <EntityRenderer shadowMapSize={quality.shadowMapSize} />
      <SnapRaycastProvider />
      <SnapIndicator />
      <EditModeRenderer />
      <BoxSelectHandler />
      <SurfaceBoxHandler />
      <SurfaceCylinderHandler />
      <SurfacePolyHandler />
      <SurfaceInsetHandler />
      <BrushSelectHandler />
      <EyedropperHandler />
      {sculptMode && (
        <>
          <SculptHandler cursorRef={sculptCursorRef} />
          <SculptCursor cursorRef={sculptCursorRef} />
        </>
      )}
      {sketchMode && (
        <>
          <SketchHandler cursorRef={sketchCursorRef} />
          <SketchCursor cursorRef={sketchCursorRef} />
        </>
      )}
      <StrokeOverlay />
      <ScreenspaceSketchOverlay />
      {showSceneInteraction && <OriginDots />}
      <SceneGizmoLayer />
      {showSceneInteraction && <SceneGrid />}
      {showSceneInteraction && <WorldOriginMarker />}
      {showSceneInteraction && <AxisIndicator />}
      <RenderPipeline allowAo={quality.allowAo} />
    </Canvas>
  );
}

/**
 * Grid plane. In perspective (or any non-axis view) renders the XY ground.
 * While locked to an axis-aligned orthographic view, the grid's plane
 * rotates to match what the user is looking at — XZ for Front/Back, YZ for
 * Left/Right, XY for Top/Bottom — mirroring Blender. Axis-line colors on
 * each variant are baked into their own NodeMaterial instance.
 *
 * The plane's local XY is always the drawn plane; world-orientation comes
 * from the mesh rotation.
 */
function referencePlaneRotation(
  viewLock: ReturnType<typeof useStore.getState>["viewLock"],
): [number, number, number] {
  if (viewLock?.axis === "y") return [-Math.PI / 2, 0, 0];
  if (viewLock?.axis === "x") return [0, Math.PI / 2, 0];
  return [0, 0, 0];
}

function surfaceOperatorHidesWorldGrid(
  state: Pick<AppState, "activeOperator" | "surfaceTool">,
): boolean {
  // Hide the world floor grid whenever any surface tool is armed or part
  // way through its modal. Box / Cylinder / Poly own their own basis grid
  // and Inset's offset-loop preview is busy enough that the world grid
  // would just add noise — keeping all four consistent here.
  if (
    state.activeOperator === "surface_box" ||
    state.activeOperator === "surface_cylinder" ||
    state.activeOperator === "surface_poly" ||
    state.activeOperator === "surface_inset"
  ) {
    return true;
  }
  return state.surfaceTool?.stage !== null && state.surfaceTool != null;
}

function SceneGrid() {
  const showGrid = useStore((s) => s.showGrid);
  const logDist = useStore((s) => s.gridLogDist);
  const viewLock = useStore((s) => s.viewLock);
  const surfaceGridActive = useStore(surfaceOperatorHidesWorldGrid);

  useEffect(() => {
    setGroundGridLogDist(logDist);
  }, [logDist]);

  if (!showGrid || surfaceGridActive) return null;

  // When locked, pick the plane that spans the two world axes *not* along
  // the view direction. Otherwise (perspective or no lock) stay on the floor.
  let material = groundGridMaterialXY;
  const rotation = referencePlaneRotation(viewLock);
  if (viewLock?.axis === "y") {
    material = groundGridMaterialXZ;
  } else if (viewLock?.axis === "x") {
    material = groundGridMaterialYZ;
  }

  return (
    <mesh
      rotation={rotation}
      userData={{ excludeFromExport: true }}
      material={material}
      renderOrder={-1}
    >
      <planeGeometry args={[GRID_EXTENT, GRID_EXTENT]} />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// World origin marker — three white axis bars crossing at (0, 0, 0)
// ---------------------------------------------------------------------------

/** Screen-space half-length of each axis bar in pixels. */
const WORLD_ORIGIN_PX = 5;

const worldOriginGeo = (() => {
  const r = 0.02; // bar thickness (thin relative to the 1-unit half-length)
  const barX = new THREE.BoxGeometry(2, r * 2, r * 2);
  const barY = new THREE.BoxGeometry(r * 2, 2, r * 2);
  const barZ = new THREE.BoxGeometry(r * 2, r * 2, 2);
  const merged = new THREE.BufferGeometry();
  // Merge all three bars into one geometry
  barX.translate(0, 0, 0);
  barY.translate(0, 0, 0);
  barZ.translate(0, 0, 0);
  const geos = [barX, barY, barZ];
  const totalVerts = geos.reduce(
    (n, g) => n + g.getAttribute("position").count,
    0,
  );
  const totalIdx = geos.reduce((n, g) => n + g.index!.count, 0);
  const pos = new Float32Array(totalVerts * 3);
  const idx = new Uint16Array(totalIdx);
  let vOff = 0,
    iOff = 0,
    vBase = 0;
  for (const g of geos) {
    const p = g.getAttribute("position").array;
    pos.set(p, vOff);
    vOff += p.length;
    for (let i = 0; i < g.index!.count; i++)
      idx[iOff++] = g.index!.array[i] + vBase;
    vBase += g.getAttribute("position").count;
  }
  merged.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  merged.setIndex(new THREE.BufferAttribute(idx, 1));
  return merged;
})();

const worldOriginMat = new THREE.MeshBasicMaterial({ color: 0xffffff });

function WorldOriginMarker() {
  const showGrid = useStore((s) => s.showGrid);
  const surfaceGridActive = useStore(surfaceOperatorHidesWorldGrid);
  const groupRef = useRef<THREE.Group>(null);
  const { camera, size: viewportSize } = useThree();

  useFrame(() => {
    const group = groupRef.current;
    if (!group) return;
    group.scale.setScalar(
      screenToWorldRadius(
        WORLD_ORIGIN_PX,
        group.position,
        camera,
        viewportSize.height,
      ),
    );
  });

  if (!showGrid || surfaceGridActive) return null;

  return (
    <group
      ref={groupRef}
      position={[0, 0, 0]}
      userData={{ excludeFromExport: true }}
    >
      <mesh geometry={worldOriginGeo} material={worldOriginMat} />
    </group>
  );
}

/**
 * HDR environment map for PBR lighting.
 * Loads a self-hosted EXR from @pmndrs/assets via lazy import.
 */
function SceneEnvironment() {
  const {
    envMapEnabled,
    envMapPreset,
    envMapIntensity,
    envMapBackground,
    viewMode,
  } = useActiveRenderConfigShallow((c) => ({
    envMapEnabled: c.envMapEnabled,
    envMapPreset: c.envMapPreset,
    envMapIntensity: c.envMapIntensity,
    envMapBackground: c.envMapBackground,
    viewMode: c.viewMode,
  }));

  // Cache loaded HDR file URLs keyed by preset name
  const [loadedFiles, setLoadedFiles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!envMapEnabled || viewMode !== "material") return;
    if (loadedFiles[envMapPreset]) return;

    const preset = ENV_MAP_PRESETS[envMapPreset];
    if (!preset) {
      return;
    }

    let cancelled = false;
    preset.load().then((url) => {
      if (!cancelled) {
        setLoadedFiles((prev) => ({ ...prev, [envMapPreset]: url }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [envMapEnabled, envMapPreset, viewMode, loadedFiles]);

  const shouldRender = envMapEnabled && viewMode === "material";
  const file = loadedFiles[envMapPreset];

  if (!shouldRender) return null;
  if (!file) return null;

  return (
    <Environment
      files={file}
      environmentIntensity={envMapIntensity}
      background={envMapBackground}
    />
  );
}

// Contact shadows removed — the per-frame gl.render() with overrideMaterial
// caused priority races with GPU compute dispatch and lost mannequin
// positionNode (sculpt displacement) during the shadow capture pass.

/**
 * Unified render pipeline — takes over rendering from R3F's default loop.
 *
 * Using useFrame at priority > 0 disables R3F's automatic gl.render() call
 * (R3F only calls the default render when state.internal.priority === 0).
 * This component is the single source of scene rendering:
 *
 * - When post-processing is active (bloom), PostProcessing.render() handles
 *   the scene render internally via its pass(scene, camera) node.
 * - When no post-processing is active, we call gl.render() directly.
 *
 * This prevents the double-render bug that would occur if separate components
 * each called gl.render() at the same priority.
 */
function RenderPipeline({ allowAo }: { allowAo: boolean }) {
  const { gl, scene, camera } = useThree();
  const {
    bloomEnabled,
    bloomIntensity,
    bloomThreshold,
    aoEnabled,
    aoIntensity,
  } = useActiveRenderConfigShallow((c) => ({
    bloomEnabled: c.bloomEnabled,
    bloomIntensity: c.bloomIntensity,
    bloomThreshold: c.bloomThreshold,
    aoEnabled: c.aoEnabled,
    aoIntensity: c.aoIntensity,
  }));
  const workspaceMode = useStore((s) => s.workspaceMode);
  const selectionOutlineCount = useStore((s) => s.selectedObjectTargets.length);
  const selectionOutlineXray = useStore(
    (s) => s.workspaceMode === "object" && s.objectXray,
  );

  const useAo = aoEnabled && allowAo;
  const useBloom = bloomEnabled;
  const supportsSelectionOutlines =
    viewportSupportsSceneInteraction(workspaceMode);
  const hasSelectionOutlines = selectionOutlineCount > 0;
  const needsPostProcessing =
    useBloom || useAo || (supportsSelectionOutlines && hasSelectionOutlines);

  const postRef = useRef<ThreePostProcessing | null>(null);
  const selectionCameraRef = useRef<THREE.Camera | null>(null);
  const aoCameraRef = useRef<THREE.Camera | null>(null);
  const selectionOutlineEnabled = useMemo(() => uniform(float(0.0)), []);

  useEffect(() => {
    selectionOutlineEnabled.value = hasSelectionOutlines ? 1.0 : 0.0;
  }, [selectionOutlineEnabled, hasSelectionOutlines]);

  useEffect(() => {
    selectionCameraRef.current = camera.clone();
    aoCameraRef.current = camera.clone();
    return () => {
      selectionCameraRef.current = null;
      aoCameraRef.current = null;
    };
  }, [camera]);

  // Build / rebuild the post-processing pipeline when effects change
  useEffect(() => {
    if (!needsPostProcessing) {
      postRef.current = null;
      return;
    }

    const post = new ThreePostProcessing(gl as any);

    const scenePass = pass(scene, camera);
    const selectionCamera = selectionCameraRef.current ?? camera;
    const aoCamera = aoCameraRef.current ?? camera;
    const selectionScenePass =
      supportsSelectionOutlines && hasSelectionOutlines
        ? pass(scene, selectionCamera)
        : null;
    // Dedicated AO scene pass with MRT {output, normal}. The camera is
    // layer-filtered to only the core scene-geometry categories (primitive,
    // mannequin, blend) so overlay MeshBasicMaterial meshes — which don't
    // emit the second color attachment and would crash WebGPU pipeline
    // creation with "THREE.writeMask is invalid" — are excluded. See
    // syncAoCamera above.
    const aoScenePass = useAo ? pass(scene, aoCamera) : null;

    if (aoScenePass) {
      aoScenePass.setMRT(mrt({ output, normal: normalView }));
    }

    const scenePassColor = scenePass.getTextureNode("output");
    let outputNode: any = scenePassColor;

    const selectionPassColor = selectionScenePass
      ? selectionScenePass.getTextureNode("output")
      : null;
    const selectionPassDepth = selectionScenePass
      ? selectionScenePass.getTextureNode("depth")
      : null;
    const scenePassDepth = scenePass.getTextureNode("depth");
    const aoPassNormal = aoScenePass
      ? aoScenePass.getTextureNode("normal")
      : null;
    const aoPassDepth = aoScenePass
      ? aoScenePass.getTextureNode("depth")
      : null;

    // Ground-Truth AO via three.js' built-in GTAONode. We feed it the
    // depth and view-space normal from a dedicated AO scene pass (rather
    // than the main scenePass) so the MRT {output, normal} attachment
    // doesn't trip the overlay MeshBasicMaterial meshes. See
    // `syncAoCamera` above for the layer-filter rationale.
    if (useAo && aoPassDepth && aoPassNormal) {
      const aoPass = ao(aoPassDepth, aoPassNormal, aoCamera);
      const aoSample = (aoPass.getTextureNode() as any).r;
      const aoFactor = (mix as any)(float(1.0), aoSample, float(aoIntensity));
      outputNode = vec4(
        (outputNode as any).rgb.mul(aoFactor),
        (outputNode as any).a,
      );
    }

    if (
      supportsSelectionOutlines &&
      hasSelectionOutlines &&
      selectionPassColor &&
      selectionPassDepth &&
      scenePassDepth
    ) {
      const texel = (float(1.0) as any).div(screenSize);
      const offsetX = (vec2 as any)(texel.x, float(0.0));
      const offsetY = (vec2 as any)(float(0.0), texel.y);

      const sampleSelection = (uv: any) => {
        const color = (selectionPassColor as any).sample(uv);
        const depth = (selectionPassDepth as any).sample(uv).r;
        const hasGeometry = (float(1.0) as any).sub(
          (smoothstep as any)(float(0.999), float(1.0), depth),
        );
        return {
          color,
          depth,
          hasGeometry,
          maskedAlpha: color.a.mul(hasGeometry),
          maskedRgb: color.rgb.mul(hasGeometry),
        };
      };

      const centerSel = sampleSelection(screenUV);
      const leftSel = sampleSelection(screenUV.sub(offsetX));
      const rightSel = sampleSelection(screenUV.add(offsetX));
      const upSel = sampleSelection(screenUV.sub(offsetY));
      const downSel = sampleSelection(screenUV.add(offsetY));

      const colorDiff = (a: any, b: any) => {
        const delta = a.sub(b);
        return delta.dot(delta).sqrt();
      };

      const edgeMagnitude = (max as any)(
        (max as any)(
          colorDiff(centerSel.maskedRgb, leftSel.maskedRgb),
          colorDiff(centerSel.maskedRgb, rightSel.maskedRgb),
        ),
        (max as any)(
          colorDiff(centerSel.maskedRgb, upSel.maskedRgb),
          colorDiff(centerSel.maskedRgb, downSel.maskedRgb),
        ),
      );

      const maxAlpha = (max as any)(
        (max as any)(
          (max as any)(centerSel.maskedAlpha, leftSel.maskedAlpha),
          (max as any)(rightSel.maskedAlpha, upSel.maskedAlpha),
        ),
        downSel.maskedAlpha,
      );
      const hasAnySelection = (smoothstep as any)(
        float(0.01),
        float(0.05),
        maxAlpha,
      );
      const edgeMask = (smoothstep as any)(
        float(0.03),
        float(0.1),
        edgeMagnitude,
      ).mul(hasAnySelection);

      const selectedDepth = (min as any)(
        (min as any)(
          (min as any)(centerSel.depth, leftSel.depth),
          (min as any)(rightSel.depth, upSel.depth),
        ),
        downSel.depth,
      );
      const sceneDepth = (scenePassDepth as any).sample(screenUV).r;
      const occluded = (step as any)(
        sceneDepth.add(float(1e-5)),
        selectedDepth,
      );

      const selectedColor = new THREE.Color(SELECTION_OUTLINE_COLOR);
      const activeColor = new THREE.Color(ACTIVE_SELECTION_OUTLINE_COLOR);
      const generatorSelectedColor = new THREE.Color(
        GENERATOR_SELECTION_OUTLINE_COLOR,
      );
      const generatorActiveColor = new THREE.Color(
        ACTIVE_GENERATOR_SELECTION_OUTLINE_COLOR,
      );
      const instanceSelectedColor = new THREE.Color(
        INSTANCE_SELECTION_OUTLINE_COLOR,
      );
      const instanceActiveColor = new THREE.Color(
        ACTIVE_INSTANCE_SELECTION_OUTLINE_COLOR,
      );
      const selectedColorNode = vec4(
        float(selectedColor.r),
        float(selectedColor.g),
        float(selectedColor.b),
        float(1.0),
      );
      const activeColorNode = vec4(
        float(activeColor.r),
        float(activeColor.g),
        float(activeColor.b),
        float(1.0),
      );
      const generatorSelectedColorNode = vec4(
        float(generatorSelectedColor.r),
        float(generatorSelectedColor.g),
        float(generatorSelectedColor.b),
        float(1.0),
      );
      const generatorActiveColorNode = vec4(
        float(generatorActiveColor.r),
        float(generatorActiveColor.g),
        float(generatorActiveColor.b),
        float(1.0),
      );
      const instanceSelectedColorNode = vec4(
        float(instanceSelectedColor.r),
        float(instanceSelectedColor.g),
        float(instanceSelectedColor.b),
        float(1.0),
      );
      const instanceActiveColorNode = vec4(
        float(instanceActiveColor.r),
        float(instanceActiveColor.g),
        float(instanceActiveColor.b),
        float(1.0),
      );
      // Three alpha bands in the selection prepass encode three variants:
      //   default:   selected=0.25, active=0.42
      //   generator: selected=0.57, active=0.70
      //   instance:  selected=0.82, active=0.95
      const instanceMask = (smoothstep as any)(
        float(0.76),
        float(0.82),
        maxAlpha,
      );
      const instanceActiveMask = (smoothstep as any)(
        float(0.88),
        float(0.93),
        maxAlpha,
      );
      const generatorMask = (max as any)(
        float(0.0),
        (smoothstep as any)(float(0.51), float(0.57), maxAlpha).sub(
          instanceMask,
        ),
      );
      const generatorActiveMask = (max as any)(
        float(0.0),
        (smoothstep as any)(float(0.63), float(0.7), maxAlpha).sub(
          instanceMask,
        ),
      );
      const defaultActiveMask = (max as any)(
        float(0.0),
        (smoothstep as any)(float(0.36), float(0.42), maxAlpha)
          .sub(generatorMask)
          .sub(instanceMask),
      );
      const defaultColorNode = (mix as any)(
        selectedColorNode,
        activeColorNode,
        defaultActiveMask,
      );
      const generatorColorNode = (mix as any)(
        generatorSelectedColorNode,
        generatorActiveColorNode,
        generatorActiveMask,
      );
      const instanceColorNode = (mix as any)(
        instanceSelectedColorNode,
        instanceActiveColorNode,
        instanceActiveMask,
      );
      const outlineColor = (mix as any)(
        (mix as any)(defaultColorNode, generatorColorNode, generatorMask),
        instanceColorNode,
        instanceMask,
      );
      const occlusionFade = selectionOutlineXray
        ? float(1.0)
        : (mix as any)(float(1.0), float(0.35), occluded);
      const outlineAlpha = edgeMask
        .mul(occlusionFade)
        .mul(selectionOutlineEnabled);
      outputNode = (mix as any)(outputNode, outlineColor, outlineAlpha);
    }

    // Bloom: additive on top
    if (useBloom) {
      const bloomPass = bloom(outputNode, bloomIntensity, 0, bloomThreshold);
      outputNode = outputNode.add(bloomPass);
    }

    post.outputNode = outputNode;
    postRef.current = post;

    return () => {
      if (postRef.current) {
        postRef.current.dispose();
      }
      postRef.current = null;
    };
  }, [
    gl,
    scene,
    camera,
    needsPostProcessing,
    useBloom,
    useAo,
    supportsSelectionOutlines,
    hasSelectionOutlines,
    bloomIntensity,
    bloomThreshold,
    aoIntensity,
    selectionOutlineXray,
    selectionOutlineEnabled,
  ]);

  // Expose a render function for export helpers. Reads postRef via closure
  // so it always gets the latest pipeline. When post-processing is active
  // this routes through PostProcessing.render(), including AO and bloom.
  useEffect(() => {
    window.__poseref_renderScene = () => {
      const post = postRef.current;
      syncInkingCameraPlanesFromSource(camera);
      if (hasSelectionOutlines && selectionCameraRef.current) {
        syncSelectionOutlineCamera(selectionCameraRef.current, camera);
      }
      if (useAo && aoCameraRef.current) {
        syncAoCamera(aoCameraRef.current, camera);
      }
      if (post) {
        post.render();
      } else {
        gl.render(scene, camera);
      }
    };
    return () => {
      delete window.__poseref_renderScene;
    };
  }, [gl, scene, camera, useAo, hasSelectionOutlines]);

  // Single render call per frame — priority 1 takes over from R3F default.
  useFrame(() => {
    const post = postRef.current;
    syncInkingCameraPlanesFromSource(camera);
    if (hasSelectionOutlines && selectionCameraRef.current) {
      syncSelectionOutlineCamera(selectionCameraRef.current, camera);
    }
    if (useAo && aoCameraRef.current) {
      syncAoCamera(aoCameraRef.current, camera);
    }
    if (post) {
      // PostProcessing.render() internally renders the scene via its
      // pass(scene, camera) node — no separate gl.render() needed.
      post.render();
    } else {
      gl.render(scene, camera);
    }
  }, 1);

  return null;
}
